/*
 * (c) Copyright, 2026, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import {
  Typography,
  Icon,
  Tooltip,
  FilterSelect,
  FilterPanel,
  Link,
  Card,
  Row,
  Col,
  Stack,
  BadgeStatus,
  Dropdown,
  EmptyState,
  TagGroup,
  Tag,
  InlineAlert
} from '@mdsol/onex-design'
import { DonutChart, BarChart } from '@mdsol/onex-design/Charts'
// @ts-expect-error - createColumnHelper is exported but not in TypeScript definitions
import { DataGrid, createColumnHelper } from '@mdsol/onex-design/DataGrid'
import { useTranslation } from 'react-i18next'
import { useServiceContext } from '../../../root'
import { useNavigateWithParams } from '../../../hooks/useNavigateWithParams'
import { transformToSelectOptions } from '~/utils/selectOptions'
import { USER_ROLE } from '../../../constants/user'
import styles from './Summary.module.css'
import LoadingSpinner from '~/components/LoadingSpinner'
import { useModal } from '~/contexts/ModalContext'
import GenerateStudyDialog from './generate-study/Dialog'
import { toast } from '~/utils/toast'
import { PAGE_SIZES_ARRAY, DEFAULT_PAGE_SIZE } from '../../../constants/pagination'
import { useFsiActionMenu } from '../../../hooks/useFsiActionMenu'
import type { ActionMenuItem } from '../../../hooks/useFsiActionMenu'
import UpdateStudyDialog from './update-study/Dialog'
import { FsiType } from '../../../constants/fsi'
import { FileAclLevel } from '../../../types/acl'
import { getDetails } from '../../../services/api/files'

const CHART_COLORS = {
  // Red/coral for negative metrics (Missing, Overdue, Failed)
  coral: 'var(--graph-apricot)',
  // Yellow/amber for warning metrics (In Progress, Due Soon, Quality Issues)
  maize: 'var(--graph-casablanca)',
  // Green/teal for positive metrics (Completed, On Time)
  sage: 'var(--graph-springmint)'
}

// Interface for saved filter preferences in localStorage
interface SavedFilterPreferences {
  levelFilter: any | null
  productFilter: any | null
  studyFilter: any | null
  environmentFilter: any | null
  countryFilter: any | null
  siteFilter: any | null
}

// Interface for saved DataGrid column configuration in localStorage
interface SavedColumnConfiguration {
  columnOrder: string[]
  columnPinning: { left: string[]; right: string[] }
  columnVisibility: Record<string, boolean>
}

interface SummaryProps {
  onFilterChange?: (filterKey: string, value: any) => void
  onCopyEmail?: () => void
  onGoToFolder?: () => void
}

// --- DataGrid Column Configuration Persistence ---
const DEFAULT_COLUMN_PINNING = { left: ['name'], right: ['actions'] }
const DEFAULT_COLUMN_VISIBILITY: Record<string, boolean> = {
  tags: false,
  milestone: false,
  stage: false,
  rule: false
}
const DEFAULT_COLUMN_ORDER: string[] = []

const Summary: React.FC<SummaryProps> = ({ onGoToFolder }) => {
  const { t } = useTranslation()
  const context = useServiceContext()
  const navigate = useNavigateWithParams()
  const Constants = (context as any).constant
  const { PRODUCT, STUDY, ENVIRONMENT, COUNTRY, SITE } = Constants.etmf.level
  const featureInfo = context.service.init.getFeaturesInfo()

  // User role check
  const USER_ROLE_CURRENT = context.service.auth.getRole()

  // Filter states
  const [levelFilter, setLevelFilter] = useState<any>(null)
  const [productFilter, setProductFilter] = useState<any>(null)
  const [studyFilter, setStudyFilter] = useState<any>(null)
  const [environmentFilter, setEnvironmentFilter] = useState<any>(null)
  const [countryFilter, setCountryFilter] = useState<any>(null)
  const [siteFilter, setSiteFilter] = useState<any>(null)
  const [email, setEmail] = useState<string>('')

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE })
  const [isLoadDataGridRows, setIsLoadDataGridRows] = useState<boolean>(false)
  const [gridData, setGridData] = useState<any>({ elements: [], totalElements: 0 })
  const [selectedCompletenessStatus, setSelectedCompletenessStatus] = useState<string | null>(null)
  const [completenessData, setCompletenessData] = useState<any[]>([])
  const [isLoadingCompleteness, setIsLoadingCompleteness] = useState<boolean>(false)
  const completenessChartRef = useRef<any>(null)

  // Timeliness chart state
  const [timelinessData, setTimelinessData] = useState<any[]>([])
  const [selectedTimelinessStatus, setSelectedTimelinessStatus] = useState<'OVERDUE' | 'DUE_SOON' | 'ON_TIME' | null>(
    null
  )
  const [isLoadingTimeliness, setIsLoadingTimeliness] = useState<boolean>(false)
  const timelinessChartRef = useRef<any>(null)

  // Quality chart state
  const [qualityData, setQualityData] = useState<any[]>([])
  const [selectedQualityStatus, setSelectedQualityStatus] = useState<'QC_FAILED' | 'QUALITY_ISSUES' | null>(null)
  const [isLoadingQuality, setIsLoadingQuality] = useState<boolean>(false)
  const qualityChartRef = useRef<any>(null)

  // Track which chart is currently active (only one can have legend selection at a time)
  const [activeChart, setActiveChart] = useState<'completeness' | 'timeliness' | 'quality' | null>(null)

  // Counter to force data refresh when clicking the same legend item again
  const [legendClickCounter, setLegendClickCounter] = useState<number>(0)

  // Global search state for DataGrid
  const [globalSearchValue, setGlobalSearchValue] = useState<string>('')

  // DataGrid filter state - managed by DataGrid, applied filters stored after "Apply" click
  const [appliedFilters, setAppliedFilters] = useState<{
    zone?: any
    section?: any
    artifact?: any
    tags?: any
    milestone?: any
    stage?: any
    rule?: any
  }>({})
  const [isSectionOptionsLoading, setIsSectionOptionsLoading] = useState<boolean>(false)
  // Pending filter selections (before Apply click) - stores full option objects with fsiId, fsiPropName, etc.
  const [pendingZoneFilter, setPendingZoneFilter] = useState<any>(null)
  const [pendingSectionFilter, setPendingSectionFilter] = useState<any>(null)
  const [pendingArtifactFilter, setPendingArtifactFilter] = useState<any>(null)
  const [pendingTagsFilter, setPendingTagsFilter] = useState<any>(null)
  const [pendingMilestoneFilter, setPendingMilestoneFilter] = useState<any>(null)
  const [pendingStageFilter, setPendingStageFilter] = useState<any>(null)
  const [pendingRuleFilter, setPendingRuleFilter] = useState<any>(null)
  const [properties, setProperties] = useState<any>({})
  // Track what triggered the last pagination change to avoid duplicate API calls
  // 'user' = user clicked pagination, 'status' = status change, 'apply' = apply filters
  const lastPaginationSourceRef = useRef<'user' | 'status' | 'apply'>('user')

  // Track if filter restoration from localStorage is in progress
  const isRestoringFiltersRef = useRef<boolean>(false)
  // Hold saved filters during cascade restoration
  const savedFiltersToRestoreRef = useRef<SavedFilterPreferences | null>(null)

  const getStorageKey = useCallback(() => {
    const userId = context.service.auth.getUserId()
    const tenantUuid = context.service.auth.getTenantUuid()
    return `summary_${userId}_${tenantUuid}`
  }, [context.service.auth])

  const saveFiltersToStorage = useCallback(() => {
    try {
      const key = getStorageKey()
      const filters: SavedFilterPreferences = {
        levelFilter,
        productFilter,
        studyFilter,
        environmentFilter,
        countryFilter,
        siteFilter
      }
      localStorage.setItem(key, JSON.stringify(filters))
    } catch (error) {
      console.error('[Summary] Error saving filters to localStorage:', error)
    }
  }, [getStorageKey, levelFilter, productFilter, studyFilter, environmentFilter, countryFilter, siteFilter])

  const loadFiltersFromStorage = useCallback((): SavedFilterPreferences | null => {
    try {
      const key = getStorageKey()
      const saved = localStorage.getItem(key)
      if (saved) {
        return JSON.parse(saved) as SavedFilterPreferences
      }
    } catch (error) {
      console.error('[Summary] Error loading filters from localStorage:', error)
    }
    return null
  }, [getStorageKey])

  const getColumnsConfigStorageKey = useCallback(() => {
    const userId = context.service.auth.getUserId()
    const tenantUuid = context.service.auth.getTenantUuid()
    return `columns-configuration_${userId}_${tenantUuid}`
  }, [context.service.auth])

  const loadColumnConfigFromStorage = useCallback((): SavedColumnConfiguration | null => {
    try {
      const key = getColumnsConfigStorageKey()
      const saved = localStorage.getItem(key)
      if (saved) {
        return JSON.parse(saved) as SavedColumnConfiguration
      }
    } catch (error) {
      console.error('[Summary] Error loading column config from localStorage:', error)
    }
    return null
  }, [getColumnsConfigStorageKey])

  const savedColumnConfig = loadColumnConfigFromStorage()

  const [columnOrder, setColumnOrder] = useState<string[]>(savedColumnConfig?.columnOrder ?? DEFAULT_COLUMN_ORDER)
  const [columnPinning, setColumnPinning] = useState<{ left: string[]; right: string[] }>(
    savedColumnConfig?.columnPinning ?? DEFAULT_COLUMN_PINNING
  )
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(
    savedColumnConfig?.columnVisibility ?? DEFAULT_COLUMN_VISIBILITY
  )

  const saveColumnConfigToStorage = useCallback(
    (config: SavedColumnConfiguration) => {
      try {
        const key = getColumnsConfigStorageKey()
        localStorage.setItem(key, JSON.stringify(config))
      } catch (error) {
        console.error('[Summary] Error saving column config to localStorage:', error)
      }
    },
    [getColumnsConfigStorageKey]
  )

  // Ref to capture the latest column order from onColumnOrderChange
  // This prevents stale closure issues when DataGrid fires both callbacks synchronously
  const pendingColumnOrderRef = useRef<string[] | null>(null)

  const handleColumnOrderChange = useCallback((newColumnOrder: string[]) => {
    setColumnOrder(newColumnOrder)
    // Store in ref - will be used by handleColumnVisibilityChange which fires immediately after
    pendingColumnOrderRef.current = newColumnOrder
  }, [])

  const handleColumnVisibilityChange = useCallback(
    (newColumnVisibility: Record<string, boolean>) => {
      setColumnVisibility(newColumnVisibility)
      // Use pending column order from ref if available (from same Apply click), otherwise use current state
      const effectiveColumnOrder = pendingColumnOrderRef.current ?? columnOrder
      // Clear the pending ref
      pendingColumnOrderRef.current = null
      saveColumnConfigToStorage({
        columnOrder: effectiveColumnOrder,
        columnPinning,
        columnVisibility: newColumnVisibility
      })
    },
    [columnOrder, columnPinning, saveColumnConfigToStorage]
  )

  // Reset all chart and grid states to initial values
  const resetAllChartAndGridStates = useCallback(() => {
    // Reset email
    setEmail('')

    // Reset pagination and grid
    setPagination({ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE })
    setIsLoadDataGridRows(false)
    setGridData({ elements: [], totalElements: 0 })

    // Reset completeness chart
    setSelectedCompletenessStatus(null)
    setCompletenessData([])
    setIsLoadingCompleteness(false)
    completenessChartRef.current = null

    // Reset timeliness chart
    setTimelinessData([])
    setSelectedTimelinessStatus(null)
    setIsLoadingTimeliness(false)
    timelinessChartRef.current = null

    // Reset quality chart
    setQualityData([])
    setSelectedQualityStatus(null)
    setIsLoadingQuality(false)
    qualityChartRef.current = null

    // Reset active chart and legend counter
    setActiveChart(null)
    setLegendClickCounter(0)

    // Reset global search
    setGlobalSearchValue('')

    // Reset DataGrid filters
    setAppliedFilters({})
    setIsSectionOptionsLoading(false)
    setPendingZoneFilter(null)
    setPendingSectionFilter(null)
    setPendingArtifactFilter(null)
    setPendingTagsFilter(null)
    setPendingMilestoneFilter(null)
    setPendingStageFilter(null)
    setPendingRuleFilter(null)

    // Reset zone and section options
    setFilteredSelectsOptions((prev) => ({
      ...prev,
      zone: [],
      section: []
    }))

    // Reset pagination source ref
    lastPaginationSourceRef.current = 'user'
  }, [])

  const { showModal } = useModal()

  const [filteredSelectsOptions, setFilteredSelectsOptions] = useState<{
    product?: any[]
    study?: any[]
    environment?: any[]
    country?: any[]
    site?: any[]
    zone?: any[]
    section?: any[]
  }>({})

  // Memoized empty filterData for controlled DataGrid filters
  const filterData = useMemo(() => ({}), [])

  const levelOptions = useMemo(
    () => [
      { value: 'all', label: t('ETMF_ALL_LEVELS') },
      { value: 'study', label: t('EMTF_MD_STUDY') },
      { value: 'country', label: t('EMTF_MD_COUNTRY') },
      { value: 'site', label: t('EMTF_MD_SITE') }
    ],
    []
  )

  // Reset all chart and grid states when required filters are incomplete
  useEffect(() => {
    // Skip reset during filter restoration from localStorage
    if (isRestoringFiltersRef.current) {
      return
    }

    const isRequiredFiltersComplete =
      (levelFilter?.value === 'all' && environmentFilter?.value) ||
      (levelFilter?.value === 'study' && environmentFilter?.value) ||
      (levelFilter?.value === 'country' && countryFilter?.value) ||
      (levelFilter?.value === 'site' && siteFilter?.value)

    if (!isRequiredFiltersComplete) {
      resetAllChartAndGridStates()
    }
  }, [
    levelFilter?.value,
    environmentFilter?.value,
    countryFilter?.value,
    siteFilter?.value,
    resetAllChartAndGridStates
  ])

  useEffect(() => {
    fetchProducts()
  }, [])

  useEffect(() => {
    Promise.all([context.api.etmf.getLinkedEntities(), context.api.files.getTagOptions(undefined, true, true)])
      .then((results) => {
        const propertiesData: any = {}
        const entityLinkMap = results[0]?.entityLinkMap
        propertiesData['tag'] = results[1]

        // Store entity link IDs
        propertiesData['artifact'] = { id: entityLinkMap?.ARTIFACT, usedValues: [], values: [] }
        propertiesData['milestone'] = { id: entityLinkMap?.MILESTONE, usedValues: [] }
        propertiesData['stage'] = { id: entityLinkMap?.STAGE, usedValues: [] }
        propertiesData['rule'] = { id: entityLinkMap?.RULE, usedValues: [] }

        // Load property values for artifact, milestone, stage, rule using propertyValuesSearch
        for (const key of ['artifact', 'milestone', 'stage', 'rule']) {
          if (propertiesData[key]?.id) {
            context.api.etmf.propertyValuesSearch(propertiesData[key].id).then((data: any) => {
              propertiesData[key].usedValues = data
              setProperties((prev: any) => ({
                ...prev,
                [key]: { ...prev[key], usedValues: data }
              }))
            })
          }
        }

        if (entityLinkMap?.ARTIFACT) {
          const artifactId =
            typeof entityLinkMap.ARTIFACT === 'string' ? parseInt(entityLinkMap.ARTIFACT, 10) : entityLinkMap.ARTIFACT
          context.api.metadata
            .getById(artifactId)
            .then((metaData: any) => {
              if (metaData?.definition?.options) {
                setProperties((prev: any) => ({
                  ...prev,
                  artifact: { ...prev.artifact, values: metaData.definition.options }
                }))
              } else {
                console.warn('[Summary] No artifact definition options found in metadata')
              }
            })
            .catch((error) => {
              console.error('[Summary] Error loading artifact metadata:', error)
            })
        }

        setProperties(propertiesData)
      })
      .catch((error) => {
        console.error('[Summary] Error loading entity links and filter options:', error)
      })
  }, [])

  useEffect(() => {
    let isLoadDataGrid: boolean = false
    // Clear email when filters change
    setEmail('')

    // Determine which filter value to use based on level
    let filterValue: string | undefined
    let shouldLoadZones = true

    if (['all', 'study'].includes(levelFilter?.value) && environmentFilter?.value) {
      filterValue = environmentFilter.value
      isLoadDataGrid = true
    } else if (levelFilter?.value === 'country' && countryFilter?.value) {
      // Don't load zones if "All Countries" is selected
      if (countryFilter.value === 'all' || countryFilter.fsiId === 'all') {
        shouldLoadZones = false
        filterValue = environmentFilter.value
      } else {
        filterValue = countryFilter.value
      }
      isLoadDataGrid = true
    } else if (levelFilter?.value === 'site' && siteFilter?.value) {
      // Don't load zones if "All Sites" is selected
      if (siteFilter.value === 'all' || siteFilter.fsiId === 'all') {
        shouldLoadZones = false
        filterValue = countryFilter.value
      } else {
        filterValue = siteFilter.value
      }
      isLoadDataGrid = true
    }
    if (filterValue && shouldLoadZones) {
      getZones(filterValue)
    } else if (!shouldLoadZones) {
      // Clear zone and section options when "All" is selected
      setFilteredSelectsOptions((prev) => ({
        ...prev,
        zone: [],
        section: []
      }))
    }
    // Fetch email if we have a valid filter value
    if (filterValue) {
      // Save current filter selections to localStorage when chart APIs are triggered
      saveFiltersToStorage()

      // Fetch Completeness status data
      const completenessLevel = levelFilter?.value === 'all' ? 'ALL' : levelFilter?.value
      setIsLoadingCompleteness(true)
      context.api.etmf
        .getStatusByLevel(completenessLevel, filterValue)
        .then((data) => {
          setCompletenessData([
            [
              `${data?.placeholdersCount || 0} ${t('ETMF_COMPLETION_TYPE_MISSING_DOCUMENTS')}`,
              data?.placeholdersCount || 0
            ],
            [`${data?.documentsInCRCount || 0} ${t('ETMF_GRAPHS_LABEL_IN_PROGRESS')}`, data?.documentsInCRCount || 0],
            [
              `${data?.effectiveDocumentsCount || 0} ${t('ETMF_GRAPHS_LABEL_COMPLETE')}`,
              data?.effectiveDocumentsCount || 0
            ]
          ])
        })
        .catch((error) => {
          toast.error(t('SUMMARY_MSG_ERROR_FETCH_STATUS', { error }))
          console.log('Failed to Fetch Status for the selected Context:', error)
        })
        .finally(() => {
          setIsLoadingCompleteness(false)
        })
      // Skip email fetch when level=all (copy email is disabled)
      if (levelFilter?.value !== 'all') {
        context.api.etmf
          .fetchEMail(filterValue)
          .then((data: any) => {
            if ((data as any)?.email) {
              setEmail((data as any).email)
            }
          })
          .catch((error) => {
            toast.error(t('SUMMARY_MSG_ERROR_FETCH_EMAIL', { error }))
            console.log('Failed to Fetch Email for the selected Context:', error)
            setEmail('')
          })
      }

      // Skip Timeliness and Quality when level=all
      if (levelFilter?.value === 'all') {
        setTimelinessData([])
        setQualityData([])
      } else {
        // Fetch Timeliness status data
        const timelinessPayload = buildPayloadForChartStatus()
        setIsLoadingTimeliness(true)
        context.api.etmf
          .getTimelinessStatus(timelinessPayload)
          .then((data: any) => {
            setTimelinessData([
              [
                `${data?.overduePlaceholders || 0} ${t('ETMF_TIMELINESS_STATUS_OVERDUE')}`,
                data?.overduePlaceholders || 0
              ],
              [
                `${data?.dueSoonPlaceholders || 0} ${t('ETMF_TIMELINESS_STATUS_DUE_SOON')}`,
                data?.dueSoonPlaceholders || 0
              ],
              [
                `${data?.documentsFiledOnTime || 0} ${t('ETMF_TIMELINESS_STATUS_ON_TIME')}`,
                data?.documentsFiledOnTime || 0
              ]
            ])
          })
          .catch((e) => {
            console.log('Failed to Fetch Timeliness Status:', e)
            setTimelinessData([])
          })
          .finally(() => {
            setIsLoadingTimeliness(false)
          })
        if (USER_ROLE_CURRENT !== USER_ROLE.ETMF_SPONSOR) {
          // Fetch Quality status data
          const qualityPayload = buildPayloadForChartStatus()
          setIsLoadingQuality(true)
          context.api.etmf
            .getQualityStatus(qualityPayload)
            .then((data: any) => {
              setQualityData([
                {
                  name: `${data?.failedCount || 0} ${t('ETMF_QUALITY_FAILED_QC_REVIEW')}`,
                  data: [data?.failedCount || 0],
                  color: CHART_COLORS.coral
                },
                {
                  name: `${data?.qcIssueCount || 0} ${t('ETMF_QUALITY_ISSUE_DETECTED')}`,
                  data: [data?.qcIssueCount || 0],
                  color: CHART_COLORS.maize
                }
              ])
            })
            .catch((e) => {
              console.log('Failed to Fetch Quality Status:', e)
              setQualityData([])
            })
            .finally(() => {
              setIsLoadingQuality(false)
            })
        }
      }
    }
  }, [
    levelFilter?.value,
    levelFilter?.value === 'all' ? environmentFilter?.value : null,
    levelFilter?.value === 'study' ? environmentFilter?.value : null,
    levelFilter?.value === 'country' ? countryFilter?.value : null,
    levelFilter?.value === 'site' ? siteFilter?.value : null
  ])

  // Attach legend click handlers for Completeness chart - memoized with functional setState
  const attachLegendHandlers = useCallback(
    (chart: any) => {
      if (!chart?.series?.[0]) return

      const statusMap: Record<string, string> = {}
      // Map translated labels to status keys
      statusMap[t('ETMF_COMPLETION_TYPE_MISSING_DOCUMENTS')] = 'NOT_STARTED'
      statusMap[t('ETMF_GRAPHS_LABEL_IN_PROGRESS')] = 'IN_PROGRESS'
      statusMap[t('ETMF_GRAPHS_LABEL_COMPLETE')] = 'COMPLETED'

      chart.series[0].data.forEach((point: any) => {
        // Remove any existing click handler to avoid duplicates
        if (point.legendItem && point.legendItem.element) {
          point.legendItem.element.onclick = null
        }
      })

      chart.series[0].data.forEach((point: any, index: number) => {
        const clickHandler = () => {
          // Find status by checking if point.name includes the translated label
          // point.name format is "12 In Progress" (count + label)
          const clickedStatus = Object.entries(statusMap).find(([label]) => point.name?.includes(label))?.[1] || null
          // Clear other chart selections when this chart is clicked
          setSelectedTimelinessStatus(null)
          setSelectedQualityStatus(null)
          // Increment counter to force refresh even if same status is clicked
          setLegendClickCounter((prev) => prev + 1)
          setSelectedCompletenessStatus((prev) => {
            const newStatus = clickedStatus
            setActiveChart(newStatus ? 'completeness' : null)
            return newStatus
          })
        }

        // Attach click handler directly to legend item DOM element
        if (point.legendItem && point.legendItem.element) {
          point.legendItem.element.style.cursor = 'pointer'
          point.legendItem.element.onclick = (e: any) => {
            e.preventDefault()
            e.stopPropagation()
            clickHandler()
            return false
          }
        }

        // Also set via point update for Highcharts internal handling
        point.update(
          {
            events: {
              legendItemClick: function (this: any) {
                clickHandler()
                return false
              }
            }
          },
          false
        )
      })
      chart.redraw()
    },
    [t]
  )

  // Attach legend click handlers for Timeliness chart
  const attachTimelinessLegendHandlers = useCallback(
    (chart: any) => {
      if (!chart?.series?.[0]) return

      const statusMap: Record<string, 'OVERDUE' | 'DUE_SOON' | 'ON_TIME'> = {}
      // Map translated labels to status keys
      statusMap[t('ETMF_TIMELINESS_STATUS_OVERDUE')] = 'OVERDUE'
      statusMap[t('ETMF_TIMELINESS_STATUS_DUE_SOON')] = 'DUE_SOON'
      statusMap[t('ETMF_TIMELINESS_STATUS_ON_TIME')] = 'ON_TIME'

      chart.series[0].data.forEach((point: any) => {
        // Remove any existing click handler to avoid duplicates
        if (point.legendItem && point.legendItem.element) {
          point.legendItem.element.onclick = null
        }
      })

      chart.series[0].data.forEach((point: any) => {
        const clickHandler = () => {
          const clickedStatus = Object.entries(statusMap).find(([label]) => point.name?.includes(label))?.[1] || null
          // Clear other chart selections when this chart is clicked
          setSelectedCompletenessStatus(null)
          setSelectedQualityStatus(null)
          // Increment counter to force refresh even if same status is clicked
          setLegendClickCounter((prev) => prev + 1)
          setSelectedTimelinessStatus((prev) => {
            const newStatus = clickedStatus
            setActiveChart(newStatus ? 'timeliness' : null)
            return newStatus
          })
        }

        // Attach click handler directly to legend item DOM element
        if (point.legendItem && point.legendItem.element) {
          point.legendItem.element.style.cursor = 'pointer'
          point.legendItem.element.onclick = (e: any) => {
            e.preventDefault()
            e.stopPropagation()
            clickHandler()
            return false
          }
        }

        // Also set via point update for Highcharts internal handling
        point.update(
          {
            events: {
              legendItemClick: function (this: any) {
                clickHandler()
                return false
              }
            }
          },
          false
        )
      })
      chart.redraw()
    },
    [t]
  )

  // Attach legend click handlers for Quality chart (BarChart)
  const attachQualityLegendHandlers = useCallback(
    (chart: any) => {
      if (!chart?.series) return

      const statusMap: Record<string, 'QC_FAILED' | 'QUALITY_ISSUES'> = {}
      // Map translated labels to status keys
      statusMap[t('ETMF_QUALITY_FAILED_QC_REVIEW')] = 'QC_FAILED'
      statusMap[t('ETMF_QUALITY_ISSUE_DETECTED')] = 'QUALITY_ISSUES'

      chart.series.forEach((series: any) => {
        // Remove any existing click handler to avoid duplicates
        if (series.legendItem && series.legendItem.element) {
          series.legendItem.element.onclick = null
        }
      })

      chart.series.forEach((series: any) => {
        const clickHandler = () => {
          // Find status by checking if series.name includes the translated label
          const clickedStatus = Object.entries(statusMap).find(([label]) => series.name?.includes(label))?.[1] || null
          // Clear other chart selections when this chart is clicked
          setSelectedCompletenessStatus(null)
          setSelectedTimelinessStatus(null)
          // Increment counter to force refresh even if same status is clicked
          setLegendClickCounter((prev) => prev + 1)
          setSelectedQualityStatus((prev) => {
            const newStatus = clickedStatus
            setActiveChart(newStatus ? 'quality' : null)
            return newStatus
          })
        }

        // Attach click handler directly to legend item DOM element
        if (series.legendItem && series.legendItem.element) {
          series.legendItem.element.style.cursor = 'pointer'
          series.legendItem.element.onclick = (e: any) => {
            e.preventDefault()
            e.stopPropagation()
            clickHandler()
            return false
          }
        }

        // Also set via series update for Highcharts internal handling
        series.update(
          {
            events: {
              legendItemClick: function (this: any) {
                clickHandler()
                return false
              }
            }
          },
          false
        )
      })
      chart.redraw()
    },
    [t]
  )

  // Re-attach handlers when data changes
  useEffect(() => {
    if (completenessChartRef.current && completenessData.length > 0) {
      attachLegendHandlers(completenessChartRef.current)
    }
  }, [completenessData, attachLegendHandlers])

  // Re-attach handlers when Timeliness data changes
  useEffect(() => {
    if (timelinessChartRef.current && timelinessData.length > 0) {
      attachTimelinessLegendHandlers(timelinessChartRef.current)
    }
  }, [timelinessData, attachTimelinessLegendHandlers])

  // Re-attach handlers when Quality data changes
  useEffect(() => {
    if (qualityChartRef.current && qualityData.length > 0) {
      attachQualityLegendHandlers(qualityChartRef.current)
    }
  }, [qualityData, attachQualityLegendHandlers])

  async function fetchProducts() {
    try {
      const products = await context.api.etmf.getSelectionsForEtmfReports(PRODUCT, undefined, true)
      const productOpts = transformToSelectOptions(products, 'fsiId', 'fsiName', null, true)
      setFilteredSelectsOptions((prev) => ({ ...prev, product: productOpts }))

      // After products load, check localStorage for saved filter preferences
      const savedFilters = loadFiltersFromStorage()
      if (savedFilters) {
        // Start restoration process
        isRestoringFiltersRef.current = true
        savedFiltersToRestoreRef.current = savedFilters

        // Restore level filter first
        if (savedFilters.levelFilter) {
          const matchedLevel = levelOptions.find((opt) => opt.value === savedFilters.levelFilter?.value)
          if (matchedLevel) {
            setLevelFilter(matchedLevel)
          } else {
            // Level no longer exists, stop restoration
            isRestoringFiltersRef.current = false
            savedFiltersToRestoreRef.current = null
            return
          }
        }

        // Restore product filter if it exists in loaded options
        if (savedFilters.productFilter) {
          const matchedProduct = productOpts.find(
            (opt: any) =>
              opt.fsiId === savedFilters.productFilter?.fsiId || opt.value === savedFilters.productFilter?.value
          )
          if (matchedProduct) {
            setProductFilter(matchedProduct)
            // Cascade: load studies for this product
            await loadStudiesForLevel(matchedProduct.value)
          } else {
            // Product no longer exists, stop restoration
            isRestoringFiltersRef.current = false
            savedFiltersToRestoreRef.current = null
          }
        } else {
          isRestoringFiltersRef.current = false
          savedFiltersToRestoreRef.current = null
        }
      }
    } catch (error) {
      console.error('Error fetching products:', error)
      isRestoringFiltersRef.current = false
      savedFiltersToRestoreRef.current = null
    }
  }

  const loadStudiesForLevel = async (productId: string) => {
    if (!productId) {
      setFilteredSelectsOptions((prev) => ({ ...prev, study: [] }))
      setStudyFilter(null)
      return
    }

    try {
      const studies = await context.api.etmf.getSelectionsForEtmfReports(STUDY, productId, true)

      const studyOpts = transformToSelectOptions(studies, 'fsiId', 'fsiName', null, true)

      setFilteredSelectsOptions((prev) => ({ ...prev, study: studyOpts }))

      // Check if we're restoring filters from localStorage
      if (isRestoringFiltersRef.current && savedFiltersToRestoreRef.current?.studyFilter) {
        const savedStudy = savedFiltersToRestoreRef.current.studyFilter
        const matchedStudy = studyOpts.find(
          (opt: any) => opt.fsiId === savedStudy?.fsiId || opt.value === savedStudy?.value
        )
        if (matchedStudy) {
          setStudyFilter(matchedStudy)
          await loadEnvironmentsForLevel(matchedStudy.value)
        } else {
          // Saved study no longer exists, stop restoration
          isRestoringFiltersRef.current = false
          savedFiltersToRestoreRef.current = null
        }
      } else if (studyOpts.length === 1) {
        setStudyFilter(studyOpts[0])
        await loadEnvironmentsForLevel(studyOpts[0]?.value)
      } else {
        setStudyFilter(null)
        setFilteredSelectsOptions((prev) => ({ ...prev, environment: [] }))
        setEnvironmentFilter(null)
      }
    } catch (error) {
      console.error('Error loading studies:', error)
      setFilteredSelectsOptions((prev) => ({ ...prev, study: [] }))
      setStudyFilter(null)
      isRestoringFiltersRef.current = false
      savedFiltersToRestoreRef.current = null
    }
  }

  const loadEnvironmentsForLevel = async (studyId: string) => {
    if (!studyId) {
      setFilteredSelectsOptions((prev) => ({ ...prev, environment: [] }))
      setEnvironmentFilter(null)
      return
    }

    try {
      const environments = await context.api.etmf.getSelectionsForEtmfReports(ENVIRONMENT, studyId, true)

      const environmentOpts = transformToSelectOptions(environments, 'fsiId', 'fsiName', null, true)

      setFilteredSelectsOptions((prev) => ({ ...prev, environment: environmentOpts }))

      // Check if we're restoring filters from localStorage
      if (isRestoringFiltersRef.current && savedFiltersToRestoreRef.current?.environmentFilter) {
        const savedEnv = savedFiltersToRestoreRef.current.environmentFilter
        const matchedEnv = environmentOpts.find(
          (opt: any) => opt.fsiId === savedEnv?.fsiId || opt.value === savedEnv?.value
        )
        if (matchedEnv) {
          setEnvironmentFilter(matchedEnv)
          // If level is country or site, continue cascade to load countries
          const savedLevel = savedFiltersToRestoreRef.current.levelFilter
          if (savedLevel?.value && ['country', 'site'].includes(savedLevel.value)) {
            await loadCountriesForLevel(matchedEnv.value)
          } else {
            // Level is 'study', restoration complete
            isRestoringFiltersRef.current = false
            savedFiltersToRestoreRef.current = null
          }
        } else {
          // Saved environment no longer exists, stop restoration
          isRestoringFiltersRef.current = false
          savedFiltersToRestoreRef.current = null
        }
      } else if (environmentOpts.length === 1) {
        setEnvironmentFilter(environmentOpts[0])
        if (levelFilter?.value && ['country', 'site'].includes(levelFilter.value)) {
          loadCountriesForLevel(environmentOpts[0]?.value)
        }
      } else if (environmentOpts.length > 1) {
        const prodEnv = environmentOpts.find((env) => env.fsiName === 'Production')
        if (prodEnv) {
          setEnvironmentFilter(prodEnv)
        }
        if (levelFilter?.value && ['country', 'site'].includes(levelFilter.value)) {
          loadCountriesForLevel(prodEnv?.value)
        }
      } else {
        setEnvironmentFilter(null)
      }
    } catch (error) {
      console.error('Error loading environments:', error)
      setFilteredSelectsOptions((prev) => ({ ...prev, environment: [] }))
      setEnvironmentFilter(null)
      isRestoringFiltersRef.current = false
      savedFiltersToRestoreRef.current = null
    }
  }

  const loadCountriesForLevel = async (environmentId: string) => {
    if (!environmentId) {
      setFilteredSelectsOptions((prev) => ({ ...prev, country: [] }))
      setCountryFilter(null)
      return
    }

    try {
      const countries = await context.api.etmf.getSelectionsForEtmfReports(COUNTRY, environmentId, true)

      let countryOpts = transformToSelectOptions(countries, 'fsiId', 'fsiName', null, true)

      // Get level from restoration or current state
      const currentLevel = isRestoringFiltersRef.current
        ? savedFiltersToRestoreRef.current?.levelFilter?.value
        : levelFilter?.value

      if (currentLevel === 'country' && countries?.length > 1) {
        const allCountriesOption = {
          fsiId: 'all',
          fsiName: context.service.filter.translate('ETMF_LABEL_SELECT_ALL_COUNTRIES'),
          fsiPropName: context.service.filter.translate('ETMF_LABEL_SELECT_ALL_COUNTRIES'),
          value: 'all',
          label: context.service.filter.translate('ETMF_LABEL_SELECT_ALL_COUNTRIES')
        }
        countryOpts = [allCountriesOption, ...countryOpts]
      }

      setFilteredSelectsOptions((prev) => ({ ...prev, country: countryOpts }))

      // Check if we're restoring filters from localStorage
      if (isRestoringFiltersRef.current && savedFiltersToRestoreRef.current?.countryFilter) {
        const savedCountry = savedFiltersToRestoreRef.current.countryFilter
        const matchedCountry = countryOpts.find(
          (opt: any) => opt.fsiId === savedCountry?.fsiId || opt.value === savedCountry?.value
        )
        if (matchedCountry) {
          setCountryFilter(matchedCountry)
          // If level is site, continue cascade to load sites
          const savedLevel = savedFiltersToRestoreRef.current.levelFilter
          if (savedLevel?.value === 'site') {
            await loadSitesForLevel(matchedCountry.value)
          } else {
            // Level is 'country', restoration complete
            isRestoringFiltersRef.current = false
            savedFiltersToRestoreRef.current = null
          }
        } else {
          // Saved country no longer exists, stop restoration
          isRestoringFiltersRef.current = false
          savedFiltersToRestoreRef.current = null
        }
      } else if (countryOpts.length === 1 && ['country', 'site'].includes(levelFilter?.value)) {
        setCountryFilter(countryOpts[0])
        if (levelFilter?.value && ['site'].includes(levelFilter.value)) {
          loadSitesForLevel(countryOpts[0]?.value)
        }
      } else {
        setCountryFilter(null)
      }
    } catch (error) {
      console.error('Error loading countries:', error)
      setFilteredSelectsOptions((prev) => ({ ...prev, country: [] }))
      setCountryFilter(null)
      isRestoringFiltersRef.current = false
      savedFiltersToRestoreRef.current = null
    }
  }

  const loadSitesForLevel = async (countryId: string) => {
    if (!countryId) {
      setFilteredSelectsOptions((prev) => ({ ...prev, site: [] }))
      setSiteFilter(null)
      return
    }

    try {
      const sites = await context.api.etmf.getSelectionsForEtmfReports(SITE, countryId, true)

      let siteOpts = transformToSelectOptions(sites, 'fsiId', 'fsiName', null, true)

      // Get level from restoration or current state
      const currentLevel = isRestoringFiltersRef.current
        ? savedFiltersToRestoreRef.current?.levelFilter?.value
        : levelFilter?.value

      if (currentLevel === 'site' && sites?.length > 1) {
        const allSitesOption = {
          fsiId: 'all',
          fsiName: context.service.filter.translate('ETMF_LABEL_SELECT_ALL_SITES'),
          fsiPropName: context.service.filter.translate('ETMF_LABEL_SELECT_ALL_SITES'),
          value: 'all',
          label: context.service.filter.translate('ETMF_LABEL_SELECT_ALL_SITES')
        }
        siteOpts = [allSitesOption, ...siteOpts]
      }

      setFilteredSelectsOptions((prev) => ({ ...prev, site: siteOpts }))

      // Check if we're restoring filters from localStorage
      if (isRestoringFiltersRef.current && savedFiltersToRestoreRef.current?.siteFilter) {
        const savedSite = savedFiltersToRestoreRef.current.siteFilter
        const matchedSite = siteOpts.find(
          (opt: any) => opt.fsiId === savedSite?.fsiId || opt.value === savedSite?.value
        )
        if (matchedSite) {
          setSiteFilter(matchedSite)
        }
        // Restoration complete for level 'site'
        isRestoringFiltersRef.current = false
        savedFiltersToRestoreRef.current = null
      } else if (siteOpts.length === 1 && levelFilter?.value === 'site') {
        // Auto-select if only one site
        setSiteFilter(siteOpts[0])
      } else {
        setSiteFilter(null)
      }
    } catch (error) {
      console.error('Error loading sites:', error)
      setFilteredSelectsOptions((prev) => ({ ...prev, site: [] }))
      setSiteFilter(null)
      isRestoringFiltersRef.current = false
      savedFiltersToRestoreRef.current = null
    }
  }

  const getZones = (fsiId: number | string | undefined) => {
    setPendingZoneFilter(null)
    return new Promise<void>((resolve, reject) => {
      context.api.etmf.getSelectionsForEtmfReports('ZONE', fsiId).then(
        (data: any) => {
          const zoneOpts = transformToSelectOptions(data, 'fsiId', 'fsiName', null, true)
          setFilteredSelectsOptions((prev) => ({ ...prev, zone: zoneOpts }))
          if (data?.length === 1) {
            getSelections(data[0]?.value)
          }
        },
        (error: any) => {
          reject()
          if (error?.data?.message) {
            toast.error(error.data.message)
          }
        }
      )
    })
  }

  function getSelections(fsiId?: string | number) {
    setIsSectionOptionsLoading(true)
    setPendingSectionFilter(null)
    setFilteredSelectsOptions((prev) => ({ ...prev, section: [] }))

    return new Promise<void>((resolve, reject) => {
      context.api.etmf.getSelectionsForEtmfReports('SECTION', fsiId).then(
        (data: any) => {
          const sectionOpts = transformToSelectOptions(data, 'fsiId', 'fsiName', null, true)
          setFilteredSelectsOptions((prev) => ({ ...prev, section: sectionOpts }))
          setIsSectionOptionsLoading(false)
          resolve()
        },
        (error: any) => {
          setIsSectionOptionsLoading(false)
          reject()
          if (error?.data?.message) {
            toast.error(error.data.message)
          }
        }
      )
    })
  }

  const buildPayloadForStudyDocuments = (
    filters: {
      zone?: any
      section?: any
      tags?: any
      artifact?: any
      milestone?: any
      stage?: any
      rule?: any
    },
    searchValue?: string
  ) => {
    let fsiId: string | number | null = null
    const DTO: any = {}

    // Capitalize first letter of level string: 'study' → 'Study'
    const capitalizeLevel = (level: string) => {
      return level.charAt(0).toUpperCase() + level.slice(1).toLowerCase()
    }

    DTO.level = levelFilter?.value ? capitalizeLevel(levelFilter.value) : 'All'

    if (productFilter) {
      DTO.product = productFilter.fsiPropName
      fsiId = productFilter.fsiId
    }
    if (studyFilter) {
      DTO.study = studyFilter.fsiPropName
      fsiId = studyFilter.fsiId
    }
    if (environmentFilter) {
      DTO.environment = environmentFilter.fsiPropName
      fsiId = environmentFilter.fsiId
    }
    if (countryFilter && countryFilter.fsiId !== 'all' && countryFilter.value !== 'all') {
      DTO.country = countryFilter.fsiPropName || countryFilter.fsiName
      fsiId = countryFilter.fsiId
    }
    if (siteFilter && siteFilter.fsiId !== 'all' && siteFilter.value !== 'all') {
      DTO.site = siteFilter.fsiPropName || siteFilter.fsiName
      fsiId = siteFilter.fsiId
    }

    // Zone filter from DataGrid applied filters
    if (filters?.zone?.fsiId) {
      DTO.zone = filters.zone.fsiPropName
      fsiId = filters.zone.fsiId
    }

    // Section filter from DataGrid applied filters
    if (filters?.section?.fsiId) {
      DTO.section = filters.section.fsiPropName
      fsiId = filters.section.fsiId
    }

    // Tags filter (array of tag values)
    if (filters?.tags && Array.isArray(filters.tags) && filters.tags.length) {
      DTO.tagIds = filters.tags.map((tag: any) => tag.value || tag)
    }

    // Artifact filter (array of artifact values)
    if (filters?.artifact && Array.isArray(filters.artifact) && filters.artifact.length) {
      DTO.artifacts = filters.artifact.map((art: any) => art.value || art)
    }

    // Milestone filter
    if (filters?.milestone) {
      DTO.milestone = filters.milestone.value || filters.milestone
    }

    // Stage filter
    if (filters?.stage) {
      DTO.stage = filters.stage.value || filters.stage
    }

    // Rule filter
    if (filters?.rule) {
      DTO.rule = filters.rule.value || filters.rule
    }

    // Use selected status from legend click, or default to 'COMPLETED'
    DTO.type = selectedCompletenessStatus || ''

    // Search filter (fsiName)
    if (searchValue && searchValue.trim()) {
      DTO.fsiName = searchValue.trim()
    }

    DTO.fsiId = fsiId
    DTO.userId = context.service.auth.getUserId()

    return DTO
  }

  const buildPayloadForChartStatus = () => {
    let fsiId: string | number | null = null
    const DTO: any = {}

    // Capitalize first letter of level string: 'study' → 'STUDY'
    DTO.level = levelFilter?.value ? levelFilter.value.toUpperCase() : 'STUDY'

    if (productFilter) {
      DTO.productProperty = productFilter.fsiPropName
      fsiId = productFilter.fsiId
    }
    if (studyFilter) {
      DTO.studyProperty = studyFilter.fsiPropName
      fsiId = studyFilter.fsiId
    }
    if (environmentFilter) {
      DTO.environmentProperty = environmentFilter.fsiPropName
      fsiId = environmentFilter.fsiId
    }
    if (countryFilter && countryFilter.fsiId !== 'all' && countryFilter.value !== 'all') {
      DTO.countryProperty = countryFilter.fsiPropName || countryFilter.fsiName
      fsiId = countryFilter.fsiId
    }
    if (siteFilter && siteFilter.fsiId !== 'all' && siteFilter.value !== 'all') {
      DTO.siteProperty = siteFilter.fsiPropName || siteFilter.fsiName
      fsiId = siteFilter.fsiId
    }

    DTO.fsiId = fsiId

    return DTO
  }

  const buildPayloadForTimelinessDocuments = (
    status: 'OVERDUE' | 'DUE_SOON' | 'ON_TIME',
    filters: {
      zone?: any
      section?: any
      tags?: any
      artifact?: any
      milestone?: any
      stage?: any
      rule?: any
    } = {},
    searchValue?: string
  ) => {
    let fsiId: string | number | null = null
    const DTO: any = {}

    DTO.level = levelFilter?.value ? levelFilter.value.toUpperCase() : 'STUDY'

    if (productFilter) {
      DTO.productProperty = productFilter.fsiPropName
      fsiId = productFilter.fsiId
    }
    if (studyFilter) {
      DTO.studyProperty = studyFilter.fsiPropName
      fsiId = studyFilter.fsiId
    }
    if (environmentFilter) {
      DTO.environmentProperty = environmentFilter.fsiPropName
      fsiId = environmentFilter.fsiId
    }
    if (countryFilter && countryFilter.fsiId !== 'all' && countryFilter.value !== 'all') {
      DTO.countryProperty = countryFilter.fsiPropName || countryFilter.fsiName
      fsiId = countryFilter.fsiId
    }
    if (siteFilter && siteFilter.fsiId !== 'all' && siteFilter.value !== 'all') {
      DTO.siteProperty = siteFilter.fsiPropName || siteFilter.fsiName
      fsiId = siteFilter.fsiId
    }

    // Zone filter from DataGrid applied filters
    if (filters?.zone?.fsiId) {
      DTO.zoneProperty = filters.zone.fsiPropName
      fsiId = filters.zone.fsiId
    }

    // Section filter from DataGrid applied filters
    if (filters?.section?.fsiId) {
      DTO.sectionProperty = filters.section.fsiPropName
      fsiId = filters.section.fsiId
    }

    // Tags filter (array of tag values)
    if (filters?.tags && Array.isArray(filters.tags) && filters.tags.length) {
      DTO.tagIds = filters.tags.map((tag: any) => tag.value || tag)
    }

    // Artifact filter
    if (filters?.artifact && Array.isArray(filters.artifact) && filters.artifact.length) {
      DTO.artifactProperty = filters.artifact.map((art: any) => art.value || art).join(',')
    }

    // Milestone filter
    if (filters?.milestone) {
      DTO.milestoneProperty = filters.milestone.value || filters.milestone
    }

    // Stage filter
    if (filters?.stage) {
      DTO.stageProperty = filters.stage.value || filters.stage
    }

    // Rule filter
    if (filters?.rule) {
      DTO.ruleProperty = filters.rule.value || filters.rule
    }

    // Timeliness status flags - only one should be true at a time
    DTO.overduePlaceholder = status === 'OVERDUE'
    DTO.dueSoonPlaceholder = status === 'DUE_SOON'
    DTO.documentsFiledOnTime = status === 'ON_TIME'

    // Search filter (fsiName)
    if (searchValue && searchValue.trim()) {
      DTO.fsiName = searchValue.trim()
    }

    DTO.fsiId = fsiId

    return DTO
  }

  const buildPayloadForQualityDocuments = (
    status: 'QC_FAILED' | 'QUALITY_ISSUES',
    filters: {
      zone?: any
      section?: any
      tags?: any
      artifact?: any
      milestone?: any
      stage?: any
      rule?: any
    } = {},
    searchValue?: string
  ) => {
    let fsiId: string | number | null = null
    const DTO: any = {}

    DTO.level = levelFilter?.value ? levelFilter.value.toUpperCase() : 'STUDY'

    if (productFilter) {
      DTO.productProperty = productFilter.fsiPropName
      fsiId = productFilter.fsiId
    }
    if (studyFilter) {
      DTO.studyProperty = studyFilter.fsiPropName
      fsiId = studyFilter.fsiId
    }
    if (environmentFilter) {
      DTO.environmentProperty = environmentFilter.fsiPropName
      fsiId = environmentFilter.fsiId
    }
    if (countryFilter && countryFilter.fsiId !== 'all' && countryFilter.value !== 'all') {
      DTO.countryProperty = countryFilter.fsiPropName || countryFilter.fsiName
      fsiId = countryFilter.fsiId
    }
    if (siteFilter && siteFilter.fsiId !== 'all' && siteFilter.value !== 'all') {
      DTO.siteProperty = siteFilter.fsiPropName || siteFilter.fsiName
      fsiId = siteFilter.fsiId
    }

    // Zone filter from DataGrid applied filters
    if (filters?.zone?.fsiId) {
      DTO.zoneProperty = filters.zone.fsiPropName
      fsiId = filters.zone.fsiId
    }

    // Section filter from DataGrid applied filters
    if (filters?.section?.fsiId) {
      DTO.sectionProperty = filters.section.fsiPropName
      fsiId = filters.section.fsiId
    }

    // Tags filter (array of tag values)
    if (filters?.tags && Array.isArray(filters.tags) && filters.tags.length) {
      DTO.tagIds = filters.tags.map((tag: any) => tag.value || tag)
    }

    // Artifact filter
    if (filters?.artifact && Array.isArray(filters.artifact) && filters.artifact.length) {
      DTO.artifactProperty = filters.artifact.map((art: any) => art.value || art).join(',')
    }

    // Milestone filter
    if (filters?.milestone) {
      DTO.milestoneProperty = filters.milestone.value || filters.milestone
    }

    // Stage filter
    if (filters?.stage) {
      DTO.stageProperty = filters.stage.value || filters.stage
    }

    // Rule filter
    if (filters?.rule) {
      DTO.ruleProperty = filters.rule.value || filters.rule
    }

    // Quality status flags - only one should be true at a time
    DTO.qcFailed = status === 'QC_FAILED'
    DTO.qualityIssues = status === 'QUALITY_ISSUES'

    // Search filter (fsiName)
    if (searchValue && searchValue.trim()) {
      DTO.fsiName = searchValue.trim()
    }

    DTO.fsiId = fsiId

    return DTO
  }

  /**
   * Load data for server-side DataGrid
   */
  /**
   * Load study documents from API
   * @param params - Sort parameters
   * @param filters - Applied filters (zone, section, etc) from DataGrid
   * @param paginationOverride - Optional explicit pagination (used during Apply to ensure page 0)
   */
  const loadStudyDocuments = (
    params: { sortBy?: string; sortDesc?: boolean },
    filters: { zone?: any; section?: any; tags?: any; artifact?: any; milestone?: any; stage?: any; rule?: any } = {},
    paginationOverride?: { pageIndex: number; pageSize: number },
    searchValue?: string
  ) => {
    const request = buildPayloadForStudyDocuments(filters, searchValue)
    params.sortBy = ''

    // Use override pagination if provided (for Apply button), otherwise use current state
    const currentPageSize = paginationOverride?.pageSize ?? pagination.pageSize
    const currentPageIndex = paginationOverride?.pageIndex ?? pagination.pageIndex

    return new Promise<{ elements: any[]; totalElements: number }>((resolve, reject) => {
      setIsLoadDataGridRows(true)
      context.api.etmf
        .getStudyDocumentsForOverviewPropertyBased(request, {
          pageSize: currentPageSize,
          pageStart: currentPageIndex,
          ...params
        })
        .then(
          (data) => {
            resolve({
              elements:
                data?.elements.map((item: any) => ({
                  trial: item?.fsiStudy || '-',
                  country: item?.fsiCountry || '-',
                  site: item?.fsiSite || '-',
                  section: item?.fsiSection || '-',
                  zone: item?.fsiZone || '-',
                  rule: item?.fsiRule || '-',
                  milestone: item?.fsiMilestone || '-',
                  environment: item?.fsiEnv || '-',
                  name: item?.fsiName || '-',
                  id: item?.fsiId || null,
                  type: item?.fsiType || '',
                  artifact: item?.fsiArtifact || '-',
                  etmfMilestone: item?.fsiMilestone || '-',
                  stage: item?.fsiStage || '-',
                  tags: item?.fsiTags || '-',
                  isDocument: !!(item?.fsiType === 'DOCUMENT'),
                  dueDateStatus: item?.fsiDueDateStatus || null
                })) ?? [],
              totalElements: data?.totalElements ?? 0
            })
          },
          (err) => {
            console.error('[Summary] API error loading study documents:', err)
            toast.error(context.service.filter.translate(err?.data?.message ? err.data.message : 'ERROR'))
            reject(err)
          }
        )
        .finally(() => {
          setIsLoadDataGridRows(false)
        })
    })
  }

  /**
   * Load Timeliness documents from API
   * @param status - Timeliness status (OVERDUE, DUE_SOON, ON_TIME)
   * @param params - Sort parameters
   * @param filters - Applied filters (zone, section, etc) from DataGrid
   * @param paginationOverride - Optional explicit pagination (used during Apply to ensure page 0)
   */
  const loadTimelinessDocuments = (
    status: 'OVERDUE' | 'DUE_SOON' | 'ON_TIME',
    params: { sortBy?: string; sortDesc?: boolean },
    filters: { zone?: any; section?: any; tags?: any; artifact?: any; milestone?: any; stage?: any; rule?: any } = {},
    paginationOverride?: { pageIndex: number; pageSize: number },
    searchValue?: string
  ) => {
    const request = buildPayloadForTimelinessDocuments(status, filters, searchValue)
    params.sortBy = ''

    const currentPageSize = paginationOverride?.pageSize ?? pagination.pageSize
    const currentPageIndex = paginationOverride?.pageIndex ?? pagination.pageIndex

    return new Promise<{ elements: any[]; totalElements: number }>((resolve, reject) => {
      setIsLoadDataGridRows(true)
      context.api.etmf
        .getStudyDocumentsForTimelinessPropertyBased(request, {
          pageSize: currentPageSize,
          pageStart: currentPageIndex,
          ...params
        })
        .then(
          (data: any) => {
            resolve({
              elements:
                data?.elements?.map((item: any) => ({
                  trial: item?.fsiStudy || '-',
                  country: item?.fsiCountry || '-',
                  site: item?.fsiSite || '-',
                  section: item?.fsiSection || '-',
                  zone: item?.fsiZone || '-',
                  rule: item?.fsiRule || '-',
                  milestone: item?.fsiMilestone || '-',
                  environment: item?.fsiEnv || '-',
                  name: item?.fsiName || '-',
                  id: item?.fsiId || null,
                  type: item?.fsiType || '',
                  artifact: item?.fsiArtifact || '-',
                  etmfMilestone: item?.fsiMilestone || '-',
                  stage: item?.fsiStage || '-',
                  tags: item?.fsiTags || '-',
                  isDocument: !!(item?.fsiType === 'DOCUMENT'),
                  dueDateStatus: item?.fsiDueDateStatus || null
                })) ?? [],
              totalElements: data?.totalElements ?? 0
            })
          },
          (err) => {
            console.error('[Summary] API error loading timeliness documents:', err)
            toast.error(context.service.filter.translate(err?.data?.message ? err.data.message : 'ERROR'))
            reject(err)
          }
        )
        .finally(() => {
          setIsLoadDataGridRows(false)
        })
    })
  }

  /**
   * Load Quality documents from API
   * @param status - Quality status (QC_FAILED, QUALITY_ISSUES)
   * @param params - Sort parameters
   * @param filters - Applied filters (zone, section, etc) from DataGrid
   * @param paginationOverride - Optional explicit pagination (used during Apply to ensure page 0)
   */
  const loadQualityDocuments = (
    status: 'QC_FAILED' | 'QUALITY_ISSUES',
    params: { sortBy?: string; sortDesc?: boolean },
    filters: { zone?: any; section?: any; tags?: any; artifact?: any; milestone?: any; stage?: any; rule?: any } = {},
    paginationOverride?: { pageIndex: number; pageSize: number },
    searchValue?: string
  ) => {
    const request = buildPayloadForQualityDocuments(status, filters, searchValue)
    params.sortBy = ''

    const currentPageSize = paginationOverride?.pageSize ?? pagination.pageSize
    const currentPageIndex = paginationOverride?.pageIndex ?? pagination.pageIndex

    return new Promise<{ elements: any[]; totalElements: number }>((resolve, reject) => {
      setIsLoadDataGridRows(true)
      context.api.etmf
        .getStudyDocumentsForQualityPropertyBased(request, {
          pageSize: currentPageSize,
          pageStart: currentPageIndex,
          ...params
        })
        .then(
          (data: any) => {
            resolve({
              elements:
                data?.elements?.map((item: any) => ({
                  trial: item?.fsiStudy || '-',
                  country: item?.fsiCountry || '-',
                  site: item?.fsiSite || '-',
                  section: item?.fsiSection || '-',
                  zone: item?.fsiZone || '-',
                  rule: item?.fsiRule || '-',
                  milestone: item?.fsiMilestone || '-',
                  environment: item?.fsiEnv || '-',
                  name: item?.fsiName || '-',
                  id: item?.fsiId || null,
                  type: item?.fsiType || '',
                  artifact: item?.fsiArtifact || '-',
                  etmfMilestone: item?.fsiMilestone || '-',
                  stage: item?.fsiStage || '-',
                  tags: item?.fsiTags || '-',
                  isDocument: !!(item?.fsiType === 'DOCUMENT'),
                  dueDateStatus: item?.fsiDueDateStatus || null
                })) ?? [],
              totalElements: data?.totalElements ?? 0
            })
          },
          (err) => {
            console.error('[Summary] API error loading quality documents:', err)
            toast.error(context.service.filter.translate(err?.data?.message ? err.data.message : 'ERROR'))
            reject(err)
          }
        )
        .finally(() => {
          setIsLoadDataGridRows(false)
        })
    })
  }

  // Handle column filters change (called when Apply button is clicked in DataGrid)
  const handleColumnFiltersChange = (filters: Record<string, any>) => {
    // Check if filters have values (filters is an object, not array)
    const hasZoneFilter = filters?.zone != null
    const hasSectionFilter = filters?.section != null
    const hasArtifactFilter = filters?.artifact != null
    const hasTagsFilter = filters?.tags != null
    const hasMilestoneFilter = filters?.milestone != null
    const hasStageFilter = filters?.stage != null
    const hasRuleFilter = filters?.rule != null

    // Clear pending states if filters were removed
    if (!hasZoneFilter && pendingZoneFilter) {
      setPendingZoneFilter(null)
      // Also clear section options since zone is cleared
      setFilteredSelectsOptions((prev) => ({ ...prev, section: [] }))
    }
    if (!hasSectionFilter && pendingSectionFilter) {
      setPendingSectionFilter(null)
    }
    if (!hasArtifactFilter && pendingArtifactFilter) {
      setPendingArtifactFilter(null)
    }
    if (!hasTagsFilter && pendingTagsFilter) {
      setPendingTagsFilter(null)
    }
    if (!hasMilestoneFilter && pendingMilestoneFilter) {
      setPendingMilestoneFilter(null)
    }
    if (!hasStageFilter && pendingStageFilter) {
      setPendingStageFilter(null)
    }
    if (!hasRuleFilter && pendingRuleFilter) {
      setPendingRuleFilter(null)
    }

    // Build applied filters using current pending states (which have full objects)
    const newAppliedFilters = {
      zone: hasZoneFilter ? pendingZoneFilter : null,
      section: hasSectionFilter ? pendingSectionFilter : null,
      artifact: hasArtifactFilter ? pendingArtifactFilter : null,
      tags: hasTagsFilter ? pendingTagsFilter : null,
      milestone: hasMilestoneFilter ? pendingMilestoneFilter : null,
      stage: hasStageFilter ? pendingStageFilter : null,
      rule: hasRuleFilter ? pendingRuleFilter : null
    }

    // Store applied filters
    setAppliedFilters(newAppliedFilters)

    // Only load data if we have an active chart selection
    if (activeChart === 'completeness' && selectedCompletenessStatus) {
      // Mark that Apply triggered pagination change (not user)
      lastPaginationSourceRef.current = 'apply'

      // Load data directly with page 0 and new filters
      loadStudyDocuments(
        { sortDesc: false },
        newAppliedFilters,
        { pageIndex: 0, pageSize: pagination.pageSize },
        globalSearchValue
      )
        .then((data) => {
          setGridData(data)
          // Reset ref to 'user' after data loads so next pagination works
          lastPaginationSourceRef.current = 'user'
        })
        .catch((err) => console.error('Failed to load data after Apply:', err))

      // Reset pagination state to page 0
      setPagination((prev) => ({ ...prev, pageIndex: 0 }))
    } else if (activeChart === 'timeliness' && selectedTimelinessStatus) {
      // Mark that Apply triggered pagination change (not user)
      lastPaginationSourceRef.current = 'apply'

      // Load data directly with page 0 and new filters
      loadTimelinessDocuments(
        selectedTimelinessStatus,
        { sortDesc: false },
        newAppliedFilters,
        {
          pageIndex: 0,
          pageSize: pagination.pageSize
        },
        globalSearchValue
      )
        .then((data) => {
          setGridData(data)
          // Reset ref to 'user' after data loads so next pagination works
          lastPaginationSourceRef.current = 'user'
        })
        .catch((err) => console.error('Failed to load timeliness data after Apply:', err))

      // Reset pagination state to page 0
      setPagination((prev) => ({ ...prev, pageIndex: 0 }))
    } else if (activeChart === 'quality' && selectedQualityStatus) {
      // Mark that Apply triggered pagination change (not user)
      lastPaginationSourceRef.current = 'apply'

      // Load data directly with page 0 and new filters
      loadQualityDocuments(
        selectedQualityStatus,
        { sortDesc: false },
        newAppliedFilters,
        {
          pageIndex: 0,
          pageSize: pagination.pageSize
        },
        globalSearchValue
      )
        .then((data) => {
          setGridData(data)
          // Reset ref to 'user' after data loads so next pagination works
          lastPaginationSourceRef.current = 'user'
        })
        .catch((err) => console.error('Failed to load quality data after Apply:', err))

      // Reset pagination state to page 0
      setPagination((prev) => ({ ...prev, pageIndex: 0 }))
    }
  }

  // Fetch data when completeness status changes (from DonutChart legend click)
  useEffect(() => {
    if (selectedCompletenessStatus) {
      // Mark that status change triggered pagination change (not user)
      lastPaginationSourceRef.current = 'status'

      // Reset pagination and load data with current applied filters
      setPagination({ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE })

      loadStudyDocuments(
        { sortDesc: false },
        appliedFilters,
        { pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE },
        globalSearchValue
      )
        .then((data) => {
          setGridData(data)
          // Reset ref to 'user' after data loads so next pagination works
          lastPaginationSourceRef.current = 'user'
        })
        .catch((err) => console.error('Failed to load data:', err))
    } else if (activeChart === null) {
      // Clear grid data when no chart has selection
      setGridData({ elements: [], totalElements: 0 })
    }
  }, [selectedCompletenessStatus, legendClickCounter])

  // Fetch data when timeliness status changes (from DonutChart legend click)
  useEffect(() => {
    if (selectedTimelinessStatus) {
      // Mark that status change triggered pagination change (not user)
      lastPaginationSourceRef.current = 'status'

      // Reset pagination and load data with current applied filters
      setPagination({ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE })

      loadTimelinessDocuments(
        selectedTimelinessStatus,
        { sortDesc: false },
        appliedFilters,
        {
          pageIndex: 0,
          pageSize: DEFAULT_PAGE_SIZE
        },
        globalSearchValue
      )
        .then((data) => {
          setGridData(data)
          // Reset ref to 'user' after data loads so next pagination works
          lastPaginationSourceRef.current = 'user'
        })
        .catch((err) => console.error('Failed to load timeliness data:', err))
    } else if (activeChart === null) {
      // Clear grid data when no chart has selection
      setGridData({ elements: [], totalElements: 0 })
    }
  }, [selectedTimelinessStatus, legendClickCounter])

  // Fetch data when quality status changes (from BarChart legend click)
  useEffect(() => {
    if (selectedQualityStatus) {
      // Mark that status change triggered pagination change (not user)
      lastPaginationSourceRef.current = 'status'

      // Reset pagination and load data with current applied filters
      setPagination({ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE })

      loadQualityDocuments(
        selectedQualityStatus,
        { sortDesc: false },
        appliedFilters,
        {
          pageIndex: 0,
          pageSize: DEFAULT_PAGE_SIZE
        },
        globalSearchValue
      )
        .then((data) => {
          setGridData(data)
          // Reset ref to 'user' after data loads so next pagination works
          lastPaginationSourceRef.current = 'user'
        })
        .catch((err) => console.error('Failed to load quality data:', err))
    } else if (activeChart === null) {
      // Clear grid data when no chart has selection
      setGridData({ elements: [], totalElements: 0 })
    }
  }, [selectedQualityStatus, legendClickCounter])

  // Fetch data when pagination changes (user-initiated only)
  useEffect(() => {
    // Skip if pagination was changed by status change or apply filters (they handle their own API calls)
    if (lastPaginationSourceRef.current !== 'user') {
      // Reset to 'user' for next pagination change
      lastPaginationSourceRef.current = 'user'
      return
    }

    // Only fetch if we have an active chart with selected status
    if (activeChart === 'completeness' && selectedCompletenessStatus) {
      loadStudyDocuments({ sortDesc: false }, appliedFilters, undefined, globalSearchValue)
        .then((data) => setGridData(data))
        .catch((err) => console.error('Failed to load data:', err))
    } else if (activeChart === 'timeliness' && selectedTimelinessStatus) {
      loadTimelinessDocuments(
        selectedTimelinessStatus,
        { sortDesc: false },
        appliedFilters,
        undefined,
        globalSearchValue
      )
        .then((data) => setGridData(data))
        .catch((err) => console.error('Failed to load timeliness data:', err))
    } else if (activeChart === 'quality' && selectedQualityStatus) {
      loadQualityDocuments(selectedQualityStatus, { sortDesc: false }, appliedFilters, undefined, globalSearchValue)
        .then((data) => setGridData(data))
        .catch((err) => console.error('Failed to load quality data:', err))
    }
  }, [pagination.pageIndex, pagination.pageSize])

  async function handleDocNameClick(id: string | number) {
    if (!id) return

    try {
      // Fetch full file details including ACL level
      const fileDetails = await getDetails(Number(id))

      // Check ACL level
      if (fileDetails.acl === FileAclLevel.NONE || fileDetails?.acl === FileAclLevel.BROWSE) {
        context.service.toast.warning(t('GENERATING_VIEWABLE_DOC_NOT_ACCESS_MSG'))
        return
      }

      // Check if file is valid
      if (!fileDetails?.valid) {
        context.service.toast.warning(t('GENERATING_VIEWABLE_DOC_NOT_VALID_MSG'))
        return
      }

      // Check if file is a placeholder
      if (fileDetails.type === FsiType.PLACEHOLDER) {
        context.service.toast.warning(t('GENERATING_VIEWABLE_DOC_PLACEHOLDER_CANNOT_OPEN'))
        return
      }

      // Check if file is a folder
      if (fileDetails.type === FsiType.FOLDER) {
        context.service.toast.warning(t('GENERATING_VIEWABLE_DOC_FOLDER_CANNOT_OPEN'))
        return
      }

      // User has access and file is valid, navigate to the file viewer
      navigate(`/show-file/${id}`)
    } catch (error: any) {
      console.error('Error fetching file details:', error)
      if (error?.status === 403) {
        context.service.toast.warning(t('GENERATING_VIEWABLE_DOC_NOT_ACCESS_MSG'))
      } else {
        context.service.toast.warning(t('ERROR_IN_GENERATING_PROCESS'))
      }
    }
  }

  const handleDataGridControlChange = useCallback((state: any) => {
    if (state.pagination) {
      setPagination(state.pagination)
    }
  }, []) // Empty dependencies - always update pagination

  const handleGlobalSearch = useCallback(
    (searchValue: string) => {
      setGlobalSearchValue(searchValue)

      // Reset pagination when searching
      setPagination((prev) => ({ ...prev, pageIndex: 0 }))

      // Reload data based on active chart with the new search value
      if (activeChart === 'completeness' && selectedCompletenessStatus) {
        loadStudyDocuments(
          { sortDesc: false },
          appliedFilters,
          { pageIndex: 0, pageSize: pagination.pageSize },
          searchValue
        )
          .then((data) => setGridData(data))
          .catch((err) => console.error('Failed to load data:', err))
      } else if (activeChart === 'timeliness' && selectedTimelinessStatus) {
        loadTimelinessDocuments(
          selectedTimelinessStatus,
          { sortDesc: false },
          appliedFilters,
          { pageIndex: 0, pageSize: pagination.pageSize },
          searchValue
        )
          .then((data) => setGridData(data))
          .catch((err) => console.error('Failed to load timeliness data:', err))
      } else if (activeChart === 'quality' && selectedQualityStatus) {
        loadQualityDocuments(
          selectedQualityStatus,
          { sortDesc: false },
          appliedFilters,
          { pageIndex: 0, pageSize: pagination.pageSize },
          searchValue
        )
          .then((data) => setGridData(data))
          .catch((err) => console.error('Failed to load quality data:', err))
      }
    },
    [
      activeChart,
      selectedCompletenessStatus,
      selectedTimelinessStatus,
      selectedQualityStatus,
      appliedFilters,
      pagination.pageSize
    ]
  )

  const handleClearSearch = useCallback(() => {
    setGlobalSearchValue('')

    // Reset pagination when clearing search
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))

    // Reload data based on active chart without search filter
    if (activeChart === 'completeness' && selectedCompletenessStatus) {
      loadStudyDocuments({ sortDesc: false }, appliedFilters, { pageIndex: 0, pageSize: pagination.pageSize }, '')
        .then((data) => setGridData(data))
        .catch((err) => console.error('Failed to load data:', err))
    } else if (activeChart === 'timeliness' && selectedTimelinessStatus) {
      loadTimelinessDocuments(
        selectedTimelinessStatus,
        { sortDesc: false },
        appliedFilters,
        { pageIndex: 0, pageSize: pagination.pageSize },
        ''
      )
        .then((data) => setGridData(data))
        .catch((err) => console.error('Failed to load timeliness data:', err))
    } else if (activeChart === 'quality' && selectedQualityStatus) {
      loadQualityDocuments(
        selectedQualityStatus,
        { sortDesc: false },
        appliedFilters,
        { pageIndex: 0, pageSize: pagination.pageSize },
        ''
      )
        .then((data) => setGridData(data))
        .catch((err) => console.error('Failed to load quality data:', err))
    }
  }, [
    activeChart,
    selectedCompletenessStatus,
    selectedTimelinessStatus,
    selectedQualityStatus,
    appliedFilters,
    pagination.pageSize
  ])

  // Calculate dynamic yAxisMax based on qualityData (sum of stacked values + padding)
  const qualityYAxisMax = useMemo(() => {
    if (!qualityData || qualityData.length === 0) return 10 // Default minimum

    // Sum all values at each data point (for stacked chart)
    const maxSum = qualityData.reduce((max, series) => {
      const seriesMax = series.data?.[0] || 0
      return max + seriesMax
    }, 0)

    // Add 20% padding and round up to next multiple of 5
    const withPadding = maxSum * 1.2
    const roundedMax = Math.ceil(withPadding / 5) * 5

    // Ensure minimum of 10
    return Math.max(roundedMax, 10)
  }, [qualityData])

  // DataGrid column helper
  const columnHelper = createColumnHelper()

  // Dynamic filter options from properties state (loaded via API on mount)
  const artifactOptions = useMemo(() => {
    // Combine both usedValues (from propertyValuesSearch) and values (from metadata definition)
    const usedValues = properties?.artifact?.usedValues || []
    const definitionValues = properties?.artifact?.values || []

    // Transform to select options format and deduplicate
    const allOptions = [...usedValues, ...definitionValues].map((item: any) => ({
      value: item.value || item,
      label: item.label || item.value || item
    }))

    // Deduplicate by value
    const uniqueOptions = allOptions.reduce((acc: any[], curr: any) => {
      if (!acc.find((opt) => opt.value === curr.value)) {
        acc.push(curr)
      }
      return acc
    }, [])

    return uniqueOptions.sort((a, b) => String(a.label).localeCompare(String(b.label)))
  }, [properties?.artifact?.usedValues, properties?.artifact?.values])

  const tagOptions = useMemo(() => {
    // Tags are already in the correct format from getTagOptions
    return (properties?.tag || []).map((tag: any) => ({
      value: tag.id || tag.value,
      label: tag.name || tag.label
    }))
  }, [properties?.tag])

  const etmfMilestoneOptions = useMemo(() => {
    return (properties?.milestone?.usedValues || []).map((item: any) => ({
      value: item.value || item,
      label: item.label || item.value || item
    }))
  }, [properties?.milestone?.usedValues])

  const stageOptions = useMemo(() => {
    return (properties?.stage?.usedValues || []).map((item: any) => ({
      value: item.value || item,
      label: item.label || item.value || item
    }))
  }, [properties?.stage?.usedValues])

  const ruleOptions = useMemo(() => {
    return (properties?.rule?.usedValues || []).map((item: any) => ({
      value: item.value || item,
      label: item.label || item.value || item
    }))
  }, [properties?.rule?.usedValues])

  // DataGrid columns configuration
  const gridColumns = useMemo(() => {
    return [
      columnHelper.accessor('name', {
        header: t('DOCUMENT_NAME'),
        id: 'name',
        sticky: 'left',
        textAlign: 'left',
        size: 400,
        minSize: 200,
        maxSize: 500,
        cell: ({ cell, row }: any) => (
          <Stack direction="horizontal" gap={2} className="align-items-center">
            {row.original?.isDocument ? (
              <Link
                key={`document-link-${cell.row.id}`}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleDocNameClick(row.original?.id)
                }}
                title={cell.getValue()}
              >
                {cell.getValue()}
              </Link>
            ) : (
              cell.getValue()
            )}
            {row.original.dueDateStatus !== 'On Track' && (
              <BadgeStatus variant={row.original.dueDateStatus === 'Overdue' ? 'critical' : 'caution'} size="sm">
                {row.original.dueDateStatus}
              </BadgeStatus>
            )}
          </Stack>
        )
      }),
      columnHelper.accessor('trial', {
        header: t('EMTF_MD_STUDY'),
        id: 'trial',
        textAlign: 'left'
      }),
      columnHelper.accessor('environment', {
        header: t('EMTF_MD_ENVIRONMENT'),
        id: 'environment',
        textAlign: 'left'
      }),
      columnHelper.accessor('country', {
        header: t('EMTF_MD_COUNTRY'),
        id: 'country',
        textAlign: 'left'
      }),
      columnHelper.accessor('site', {
        header: t('EMTF_MD_SITE'),
        id: 'site',
        textAlign: 'left'
      }),
      columnHelper.accessor('zone', {
        header: t('EMTF_ZONE'),
        id: 'zone',
        textAlign: 'left',
        defaultFilter: true,
        Filter: ({ onChange, displayApplyButton, ...props }: any) => {
          // Disable zone filter when "All Countries" or "All Sites" is selected
          const isDisabled =
            (levelFilter?.value === 'country' && (countryFilter?.fsiId === 'all' || countryFilter?.value === 'all')) ||
            (levelFilter?.value === 'site' && (siteFilter?.fsiId === 'all' || siteFilter?.value === 'all'))

          return (
            <FilterSelect
              label={t('EMTF_ZONE')}
              options={filteredSelectsOptions?.zone || []}
              searchPlaceholder="Search"
              value={pendingZoneFilter ? [pendingZoneFilter] : []}
              onChange={(option: any) => {
                // Store full option object for use in Apply handler (has fsiId, fsiPropName, etc.)
                const selectedOption = option && option.length > 0 ? option[0] : null
                setPendingZoneFilter(selectedOption)

                // Call DataGrid's onChange to register the filter value
                onChange(option)

                // Load section options when zone is selected
                if (selectedOption?.value) {
                  getSelections(selectedOption.value)
                } else {
                  // Clear section options and pending section filter when zone is cleared
                  setPendingSectionFilter(null)
                  setFilteredSelectsOptions((prev) => ({
                    ...prev,
                    section: []
                  }))
                }
              }}
              size="sm"
              disabled={isDisabled}
              placeholder={isDisabled ? t('ETMF_NOT_AVAILABLE_FOR_ALL') : t('ETMF_SELECT_ZONE')}
            />
          )
        }
      }),
      columnHelper.accessor('section', {
        header: t('EMTF_SECTION'),
        id: 'section',
        textAlign: 'left',
        defaultFilter: true,
        Filter: ({ onChange, displayApplyButton, ...props }: any) => {
          // Disable section filter when "All Countries" or "All Sites" is selected
          const isDisabledDueToAll =
            (levelFilter?.value === 'country' && (countryFilter?.fsiId === 'all' || countryFilter?.value === 'all')) ||
            (levelFilter?.value === 'site' && (siteFilter?.fsiId === 'all' || siteFilter?.value === 'all'))

          const isDisabled =
            isDisabledDueToAll || (!filteredSelectsOptions?.section?.length && !isSectionOptionsLoading)
          const placeholder = isDisabledDueToAll
            ? t('ETMF_NOT_AVAILABLE_FOR_ALL')
            : isSectionOptionsLoading
              ? t('LOADING')
              : t('ETMF_SELECT_ZONE_FIRST')

          return (
            <FilterSelect
              label={t('EMTF_SECTION')}
              options={filteredSelectsOptions?.section || []}
              searchPlaceholder="Search"
              value={pendingSectionFilter ? [pendingSectionFilter] : []}
              onChange={(option: any) => {
                // Store full option object for use in Apply handler (has fsiId, fsiPropName, etc.)
                const selectedOption = option && option.length > 0 ? option[0] : null
                setPendingSectionFilter(selectedOption)
                onChange(option)
              }}
              size="sm"
              disabled={isDisabled}
              isLoading={isSectionOptionsLoading}
              placeholder={placeholder}
            />
          )
        }
      }),
      columnHelper.accessor('artifact', {
        header: t('TABLE_TH_ARTIFACT_NO'),
        id: 'artifact',
        textAlign: 'left',
        defaultFilter: true,
        Filter: ({ onChange, displayApplyButton, ...props }: any) => (
          <FilterSelect
            label={t('TABLE_TH_ARTIFACT_NO')}
            options={artifactOptions}
            searchPlaceholder="Search"
            value={pendingArtifactFilter || []}
            isMulti
            onChange={(option: any) => {
              // Store full option array for use in Apply handler
              setPendingArtifactFilter(option || [])
              onChange(option)
            }}
            size="sm"
          />
        )
      }),
      columnHelper.accessor('tags', {
        header: t('TAG_FILTER'),
        id: 'tags',
        textAlign: 'left',
        size: 200,
        minSize: 200,
        maxSize: 200,
        defaultFilter: true,
        cell: ({ row }: any) => {
          const tags = row?.original?.tags

          if (!tags || tags.length === 0) {
            return null
          }

          if (tags === '-') {
            return tags
          }

          return (
            <div className={styles.tagsGroupContainer}>
              <TagGroup truncate>
                {tags?.length
                  ? tags?.split(',')?.map((tag: any, index: number) => (
                      <Tag controlId={`${index}-${row?.original?.tags}`} key={index} variant={null} size="sm">
                        {tag}
                      </Tag>
                    ))
                  : null}
              </TagGroup>
            </div>
          )
        },
        Filter: ({ onChange, displayApplyButton, ...props }: any) => (
          <FilterSelect
            label={t('TAG_FILTER')}
            options={tagOptions}
            searchPlaceholder="Search"
            value={pendingTagsFilter || []}
            isMulti
            onChange={(option: any) => {
              // Store full option array for use in Apply handler
              setPendingTagsFilter(option || [])
              onChange(option)
            }}
            size="sm"
          />
        )
      }),
      columnHelper.accessor('etmfMilestone', {
        header: t('ETMF_OVERVIEW_MILESTONE'),
        id: 'milestone',
        textAlign: 'left',
        defaultFilter: true,
        Filter: ({ onChange, displayApplyButton, ...props }: any) => (
          <FilterSelect
            label={t('ETMF_OVERVIEW_MILESTONE')}
            options={etmfMilestoneOptions}
            searchPlaceholder="Search"
            value={pendingMilestoneFilter ? [pendingMilestoneFilter] : []}
            onChange={(option: any) => {
              // Store full option object for use in Apply handler
              const selectedOption = option && option.length > 0 ? option[0] : null
              setPendingMilestoneFilter(selectedOption)
              onChange(option)
            }}
            size="sm"
          />
        )
      }),
      columnHelper.accessor('stage', {
        header: t('EMTF_STAGE'),
        id: 'stage',
        textAlign: 'left',
        defaultFilter: true,
        Filter: ({ onChange, displayApplyButton, ...props }: any) => (
          <FilterSelect
            label={t('EMTF_STAGE')}
            options={stageOptions}
            searchPlaceholder="Search"
            value={pendingStageFilter ? [pendingStageFilter] : []}
            onChange={(option: any) => {
              // Store full option object for use in Apply handler
              const selectedOption = option && option.length > 0 ? option[0] : null
              setPendingStageFilter(selectedOption)
              onChange(option)
            }}
            size="sm"
          />
        )
      }),
      columnHelper.accessor('rule', {
        header: t('EMTF_RULE'),
        id: 'rule',
        textAlign: 'left',
        defaultFilter: true,
        Filter: ({ onChange, displayApplyButton, ...props }: any) => (
          <FilterSelect
            label={t('EMTF_RULE')}
            options={ruleOptions}
            searchPlaceholder="Search"
            value={pendingRuleFilter ? [pendingRuleFilter] : []}
            onChange={(option: any) => {
              // Store full option object for use in Apply handler
              const selectedOption = option && option.length > 0 ? option[0] : null
              setPendingRuleFilter(selectedOption)
              onChange(option)
            }}
            size="sm"
          />
        )
      }),
      // Custom actions column using Dropdown with items prop for submenu support
      columnHelper.display({
        id: 'actions',
        header: '',
        size: 30,
        minSize: 30,
        maxSize: 30,
        meta: {
          sticky: 'right'
        },
        cell: ({ row }: any) => {
          return <SummaryTableMenu row={row} />
        }
      })
    ]
  }, [
    artifactOptions,
    tagOptions,
    etmfMilestoneOptions,
    stageOptions,
    ruleOptions,
    filteredSelectsOptions,
    isSectionOptionsLoading,
    pendingZoneFilter,
    pendingSectionFilter,
    pendingArtifactFilter,
    pendingTagsFilter,
    pendingMilestoneFilter,
    pendingStageFilter,
    pendingRuleFilter
  ])
  // Transform ActionMenuItem[] from useFsiActionMenu to onex-design Dropdown items format
  // This enables submenu support via subItems prop
  const transformToDropdownItems = (
    menuItems: ActionMenuItem[],
    t: (key: string) => string
  ): Array<{
    eventKey: string
    title: string
    disabled?: boolean
    hasDividerAfter?: boolean
    onClick?: () => void
    subItems?: Array<{ eventKey: string; title: string; disabled?: boolean; onClick?: () => void }>
  }> => {
    const result = menuItems
      .filter((item) => !item.divider) // Filter out divider-only items, we'll handle them via hasDividerAfter
      .map((item, index, filteredArray) => {
        // Find the original index to check for dividers after
        const originalIndex = menuItems.indexOf(item)
        const nextOriginalItem = menuItems[originalIndex + 1]
        const hasDividerAfter = nextOriginalItem?.divider === true

        const dropdownItem: any = {
          eventKey: item.value || `item-${index}`,
          title: item.value ? t(item.value) : '',
          disabled: item.disabled,
          hasDividerAfter
        }

        // Handle submenu items recursively
        if (item.submenu && item.submenu.length > 0) {
          dropdownItem.subItems = item.submenu.map((subItem, subIndex) => ({
            eventKey: subItem.value || `subitem-${subIndex}`,
            title: subItem.value ? t(subItem.value) : '',
            disabled: subItem.disabled,
            onClick: subItem.action
          }))
          // Don't add onClick to parent item if it has subItems
        } else {
          // Only add onClick to items without subItems
          dropdownItem.onClick = item.action
        }

        return dropdownItem
      })

    return result
  }

  // Helper component for row action menu using onex-design Dropdown with items prop
  // This pattern supports nested menus via subItems
  const SummaryTableMenu: React.FC<{ row: any }> = ({ row }) => {
    const { t } = useTranslation()
    const fsiItem = {
      id: row.original.id,
      name: row.original.name,
      type: row.original.type || (row.original.isDocument ? 'DOCUMENT' : 'FOLDER')
    } as any

    const { items, isOpen, setIsOpen } = useFsiActionMenu(fsiItem, {})

    // Check if loading (useFsiActionMenu returns [{value: 'LOADING...'}] during loading)
    const isLoading = items.length === 1 && items[0]?.value === 'LOADING...'

    // Transform ActionMenuItem[] to onex-design Dropdown items format
    const dropdownItems = useMemo(() => {
      if (isLoading) return []
      if (items.length === 0) {
        return [{ eventKey: 'no-action', title: t('ACTION_MENU_NO_ACTION_AVAILABLE'), disabled: true }]
      }
      const transformed = transformToDropdownItems(items, t)
      return transformed
    }, [items, isLoading, t])

    return (
      <Dropdown
        id={`action-menu-summary-${row.original.id}`}
        dataTestId={`action-menu-summary-${row.original.id}`}
        align="end"
        size="sm"
        variant="tertiary"
        buttonStyle="icon"
        iconType="more_vert"
        items={dropdownItems}
        isLoading={isLoading}
        show={isOpen}
        onToggle={(nextShow: any) => setIsOpen(Boolean(nextShow))}
        renderOnMount={true}
        menuPortalTarget={document.body as any}
        popperConfig={{
          strategy: 'absolute',
          modifiers: [
            {
              name: 'preventOverflow',
              options: {
                boundary: 'viewport',
                padding: 8
              }
            }
          ]
        }}
      />
    )
  }

  const handleCopyEmail = async () => {
    if (!email) {
      console.warn('No email address available to copy')
      return
    }

    try {
      await navigator.clipboard.writeText(email)
      toast.success(t('SUMMARY_MSG_INFO_EMAIL_COPIED'))
    } catch (error) {
      console.error('Failed to copy email to clipboard:', error)
      toast.error(t('SUMMARY_MSG_ERROR_COPY_EMAIL'))
    }
  }

  /**
   * Check if user can navigate to parent folder
   */
  const canGoToFolder = (): boolean => {
    if (!levelFilter?.value) return false

    switch (levelFilter.value) {
      case 'all':
      case 'study':
        return !!environmentFilter
      case 'country':
        return !!countryFilter
      case 'site':
        return !!siteFilter
      default:
        return false
    }
  }

  const canCopyEmail = (): boolean => {
    if (!levelFilter?.value) return false
    if (environmentFilter?.label !== 'Production') return false
    if (countryFilter?.fsiId === 'all') return false
    if (siteFilter?.fsiId === 'all') return false

    switch (levelFilter.value) {
      case 'study':
        return !!environmentFilter?.value
      case 'country':
        return !!countryFilter?.value
      case 'site':
        return !!siteFilter?.value
      default:
        return false
    }
  }

  const folderRedirect = (targetObject: any) => {
    if (targetObject?.value) {
      navigate(`/browser/${targetObject.value}`)
    } else {
      toast.warning(t('ETMF_MSG_ERROR_GET_CLINICAL_FOLDER'))
    }
  }

  const goToFolder = () => {
    let targetObject: any = null

    switch (levelFilter?.value) {
      case 'all':
        targetObject = environmentFilter
        break
      case 'environment':
        targetObject = environmentFilter
        break
      case 'study':
        targetObject = environmentFilter
        break
      case 'country':
        if ((countryFilter as any)?.value === 'all') {
          // Get first country's parent folder
          const firstCountry = filteredSelectsOptions.country?.[1]
          if (firstCountry?.fsiId) {
            context.api.files.getDetails(Number(firstCountry?.fsiId)).then((data: any) => {
              folderRedirect({ value: data?.parent?.id })
            })
          }
          return
        }
        targetObject = countryFilter
        break
      case 'site':
        if ((siteFilter as any)?.value === 'all') {
          // Get first site's parent folder
          const firstSite = filteredSelectsOptions.site?.[1]
          if (firstSite?.fsiId) {
            context.api.files.getDetails(Number(firstSite?.fsiId)).then((data: any) => {
              folderRedirect({ value: data?.parent?.id })
            })
          }
          return
        }
        targetObject = siteFilter
        break
    }

    if (targetObject) {
      folderRedirect(targetObject)
    }
  }

  const handleGoToFolder = () => {
    onGoToFolder?.()
  }

  function getLastFsiId() {
    let fsiId: string | number | null = null
    if (productFilter) {
      fsiId = productFilter.fsiId
    }
    if (studyFilter) {
      fsiId = studyFilter.fsiId
    }
    if (environmentFilter) {
      fsiId = environmentFilter.fsiId
    }
    if (countryFilter && countryFilter.fsiId !== 'all' && countryFilter.value !== 'all') {
      fsiId = countryFilter.fsiId
    }
    if (siteFilter && siteFilter.fsiId !== 'all' && siteFilter.value !== 'all') {
      fsiId = siteFilter.fsiId
    }

    if (appliedFilters?.zone?.fsiId) {
      fsiId = appliedFilters.zone.fsiId
    }

    if (appliedFilters?.section?.fsiId) {
      fsiId = appliedFilters.section.fsiId
    }
    return fsiId
  }

  function navigateToUploadNFilePath() {
    const parentFsiId = getLastFsiId()
    const path = parentFsiId ? `/upload-and-file/${parentFsiId}` : '/upload-and-file'

    // Pass level override as query param
    const params: Record<string, string> = {}
    if (levelFilter?.value === 'all') {
      params.level = 'all'
    } else if (siteFilter?.value === 'all' || siteFilter?.fsiId === 'all') {
      params.level = 'site'
    } else if (countryFilter?.value === 'all' || countryFilter?.fsiId === 'all') {
      params.level = 'country'
    }

    navigate(path, Object.keys(params).length ? { params } : undefined)
  }

  const isLevelSelected = Boolean(levelFilter?.value)

  return (
    <div className="etmf-summary-onex sub-page-header mcc-content-frame" id="etmf-summary">
      <Stack direction="horizontal" gap={2} className="mb-3 align-items-center justify-content-between">
        <Stack direction="horizontal" gap={2} className="align-items-center">
          <Typography variant="h2">{t('EOS_ACTIVITY_SUMMARY')}</Typography>
        </Stack>
        {/* Role-based action menu - matches ActionsMenuWrapper.tsx logic */}
        {USER_ROLE_CURRENT &&
          USER_ROLE_CURRENT !== USER_ROLE.ETMF_SPONSOR &&
          (() => {
            const menuItems = []

            // ETMF_SPECIALIST: Only Upload action
            if (USER_ROLE_CURRENT === USER_ROLE.ETMF_SPECIALIST) {
              menuItems.push({
                eventKey: 'upload-and-file',
                title: t('UPLOAD_AND_FILE_ACTION_ITEM'),
                onClick: () => navigateToUploadNFilePath()
              })
            } else {
              // For all other roles: Generate + Update actions
              // ADMIN/ETMF_MANAGER/CONTENT_ADMIN also get Upload action
              if ([USER_ROLE.ADMIN, USER_ROLE.ETMF_MANAGER, USER_ROLE.CONTENT_ADMIN].includes(USER_ROLE_CURRENT)) {
                menuItems.push({
                  eventKey: 'upload-and-file',
                  title: t('UPLOAD_AND_FILE_ACTION_ITEM'),
                  onClick: () => navigateToUploadNFilePath()
                })
              }

              menuItems.push(
                {
                  eventKey: 'export-report',
                  title: t('ADMIN_ACTION_ETMF_GENERATE_STUDY_STRC'),
                  onClick: () => showModal(<GenerateStudyDialog onClose={() => {}} />)
                },
                {
                  eventKey: 'schedule-report',
                  title: t('ADMIN_UPDATE_STRUCTURES'),
                  onClick: () => showModal(<UpdateStudyDialog onClose={() => {}} />)
                }
              )
            }

            return menuItems.length > 0 ? (
              <Dropdown
                id="summary-actions"
                title={t('ACTIONS')}
                variant="primary"
                size="sm"
                hideDefaultIcon
                className="summary-onex-action-menu"
                items={menuItems}
              />
            ) : null
          })()}
      </Stack>
      {!isLevelSelected ? (
        <Stack className="mb-3" style={{ width: '60%' }}>
          <InlineAlert
            className={styles.summaryInlineAlertPrimaryBorder}
            size="sm"
            show
            title={t('ETMF_FILTER_INSTRUCTIONS')}
          />
        </Stack>
      ) : null}
      <FilterPanel className="mb-3 align-items-center">
        <FilterPanel.Filters>
          <FilterSelect
            size="sm"
            label={t('EMTF_LEVEL')}
            displayApplyButton={false}
            options={levelOptions}
            value={levelFilter}
            isSearchable
            onChange={(option) => {
              setLevelFilter(option[0])
              setProductFilter(null)
              setStudyFilter(null)
              setEnvironmentFilter(null)
              setCountryFilter(null)
              setSiteFilter(null)
              setFilteredSelectsOptions((prev) => ({
                ...prev,
                study: [],
                environment: [],
                country: [],
                site: [],
                zone: [],
                section: []
              }))
            }}
          />
          <FilterSelect
            size="sm"
            label={t('EMTF_MD_PRODUCT')}
            displayApplyButton={false}
            isSearchable={true}
            isMulti={false}
            options={filteredSelectsOptions.product || []}
            value={productFilter}
            disabled={!levelFilter?.value || !['all', 'study', 'country', 'site'].includes(levelFilter?.value)}
            onChange={(option) => {
              setProductFilter(option[0])
              // Clear dependent filters and options
              setStudyFilter(null)
              setEnvironmentFilter(null)
              setCountryFilter(null)
              setSiteFilter(null)
              setFilteredSelectsOptions((prev) => ({
                ...prev,
                study: [],
                environment: [],
                country: [],
                site: [],
                zone: [],
                section: []
              }))
              // Trigger cascade to load new studies
              if (option[0]?.value) {
                loadStudiesForLevel(option[0].value)
              }
            }}
          />
          <FilterSelect
            size="sm"
            label={t('EMTF_MD_STUDY')}
            displayApplyButton={false}
            isSearchable={true}
            options={filteredSelectsOptions.study || []}
            value={studyFilter}
            disabled={
              !levelFilter?.value ||
              !['all', 'study', 'country', 'site'].includes(levelFilter?.value) ||
              !productFilter ||
              !filteredSelectsOptions.study?.length
            }
            onChange={(option) => {
              setStudyFilter(option[0])
              // Clear dependent filters and options
              setEnvironmentFilter(null)
              setCountryFilter(null)
              setSiteFilter(null)
              setFilteredSelectsOptions((prev) => ({
                ...prev,
                environment: [],
                country: [],
                site: [],
                zone: [],
                section: []
              }))
              // Trigger cascade to load new environments
              if (option?.[0]?.value) {
                loadEnvironmentsForLevel(option?.[0]?.value)
              }
            }}
          />
          <FilterSelect
            size="sm"
            label={t('EMTF_MD_ENVIRONMENT')}
            displayApplyButton={false}
            isSearchable={true}
            options={filteredSelectsOptions.environment || []}
            value={environmentFilter}
            disabled={!levelFilter?.value || !studyFilter}
            onChange={(option) => {
              setEnvironmentFilter(option[0])
              // Clear dependent filters and options
              setCountryFilter(null)
              setSiteFilter(null)
              setFilteredSelectsOptions((prev) => ({ ...prev, country: [], site: [], zone: [], section: [] }))
              // Trigger cascade to load new countries (only for country/site levels)
              if (option[0]?.value && ['country', 'site'].includes(levelFilter?.value)) {
                loadCountriesForLevel(option[0].value)
              }
            }}
          />
          {['country', 'site'].includes(levelFilter?.value) ? (
            <FilterSelect
              size="sm"
              label={t('EMTF_MD_COUNTRY')}
              displayApplyButton={false}
              isSearchable={true}
              options={filteredSelectsOptions.country || []}
              value={countryFilter}
              disabled={!levelFilter?.value || !environmentFilter?.value}
              onChange={(option) => {
                setCountryFilter(option[0])
                // Clear dependent filters and options
                setSiteFilter(null)
                setFilteredSelectsOptions((prev) => ({ ...prev, site: [], zone: [], section: [] }))
                // Trigger cascade to load new sites (only for site level)
                if (option?.[0]?.value && levelFilter?.value === 'site') {
                  loadSitesForLevel(option?.[0].value)
                }
              }}
            />
          ) : null}
          {levelFilter?.value === 'site' ? (
            <FilterSelect
              size="sm"
              label={t('EMTF_MD_SITE')}
              displayApplyButton={false}
              isSearchable={true}
              options={filteredSelectsOptions.site || []}
              value={siteFilter}
              disabled={!levelFilter?.value || !countryFilter?.value}
              onChange={(option) => {
                if (option?.[0]?.value) {
                  setSiteFilter(option[0])
                } else {
                  setSiteFilter(null)
                }
                setFilteredSelectsOptions((prev) => ({ ...prev, zone: [], section: [] }))
              }}
            />
          ) : null}
        </FilterPanel.Filters>
        <FilterPanel.EndButtons>
          <Stack direction="horizontal" gap={3} className="align-items-center">
            <Link
              key="copy-email"
              href="#"
              leadingIcon={<Icon>alternate_email</Icon>}
              disabled={!canCopyEmail() || !email}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleCopyEmail()
              }}
              title={email ? `${t('ADMIN_ACTION_COPY')} ${email}` : t('ETMF_NO_EMAIL_AVAILABLE')}
            >
              {t('ETMF_COPY_EMAIL')}
            </Link>
            <Link
              key="go-to-folder"
              leadingIcon={<Icon>open_in_new</Icon>}
              disabled={!canGoToFolder()}
              onClick={(event) => {
                event.preventDefault()
                goToFolder()
              }}
              title={
                canGoToFolder()
                  ? t('ETMF_TOOLTIP_GO_TO_PARENT_FOLDER')?.replace(/<[^>]*>/g, '') || t('ETMF_GO_TO_FOLDER')
                  : t('ETMF_SELECT_FILTERS_FOR_FOLDER')
              }
            >
              {t('ETMF_GO_TO_FOLDER')}
            </Link>
          </Stack>
        </FilterPanel.EndButtons>
      </FilterPanel>

      {/* Summary Cards */}
      <Row>
        {/* Completeness Card */}
        <Col md={4}>
          <Card variant="widget" showTitleBar title={t('ETMF_COMPLETENESS')} titleActionVariant="none">
            <Card.Body className={styles.cardBodyRelative}>
              {isLoadingCompleteness && (
                <div className={styles.loadingOverlay}>
                  <LoadingSpinner size={40} />
                </div>
              )}
              {completenessData?.length > 0 ? (
                <DonutChart
                  data={completenessData}
                  colors={[CHART_COLORS.coral, CHART_COLORS.maize, CHART_COLORS.sage]}
                  annotationText={t('ETMF_ITEMS')}
                  height={250}
                  width={350}
                  legendLayout="horizontal"
                  innerSize="65%"
                  tooltipOptions={{
                    outside: true,
                    padding: 8
                  }}
                  callback={(chart) => {
                    completenessChartRef.current = chart
                    // First update legend styling, then attach handlers after redraw
                    chart.update(
                      {
                        legend: {
                          layout: 'horizontal',
                          maxHeight: 50,
                          itemDistance: 10,
                          itemMarginBottom: 0
                        }
                      },
                      true
                    )
                    setTimeout(() => attachLegendHandlers(chart), 150)
                  }}
                />
              ) : (
                <div className={styles.noDataContainer}>
                  <EmptyState
                    variant="card"
                    defaultImgVariant={'chartDonut'}
                    title={t(isLevelSelected ? 'NO_DATA_TO_SHOW' : 'EMPTY_CHART_DATA')}
                    subtitle={isLevelSelected ? t('PLEASE_SELECT_CONTEXT') : undefined}
                  />
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Timeliness Card */}
        <Col md={4}>
          <Card variant="widget" showTitleBar title={t('ETMF_TIMELINESS')} titleActionVariant="none">
            <Card.Body className={styles.cardBodyRelative}>
              {levelFilter?.value === 'all' ? (
                <div className={styles.noDataContainer}>
                  <EmptyState
                    variant="card"
                    defaultImgVariant={'chartDonut'}
                    title={t('ETMF_ALL_LEVELS_UNABLE_TO_DISPLAY_CHART')}
                    subtitle={t('ETMF_ALL_LEVELS_SELECT_OTHER_LEVEL')}
                  />
                </div>
              ) : (
                <>
                  {isLoadingTimeliness && (
                    <div className={styles.loadingOverlay}>
                      <LoadingSpinner size={40} />
                    </div>
                  )}
                  {timelinessData?.length > 0 ? (
                    <DonutChart
                      data={timelinessData}
                      colors={[CHART_COLORS.coral, CHART_COLORS.maize, CHART_COLORS.sage]}
                      annotationText={t('ETMF_TOTAL')}
                      height={250}
                      width={300}
                      legendLayout="horizontal"
                      innerSize="65%"
                      tooltipOptions={{
                        outside: true,
                        padding: 8
                      }}
                      callback={(chart) => {
                        timelinessChartRef.current = chart
                        // First update legend styling, then attach handlers after redraw
                        chart.update(
                          {
                            legend: {
                              layout: 'horizontal',
                              maxHeight: 50,
                              itemDistance: 10,
                              itemMarginBottom: 0
                            }
                          },
                          true
                        )
                        // Attach legend handlers after chart update completes
                        setTimeout(() => attachTimelinessLegendHandlers(chart), 150)
                      }}
                    />
                  ) : (
                    <div className={styles.noDataContainer}>
                      <EmptyState
                        variant="card"
                        defaultImgVariant={'chartDonut'}
                        title={t(isLevelSelected ? 'NO_DATA_TO_SHOW' : 'EMPTY_CHART_DATA')}
                        subtitle={isLevelSelected ? t('PLEASE_SELECT_CONTEXT') : undefined}
                      />
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Quality Card */}
        <Col md={4}>
          <Card variant="widget" showTitleBar title={t('ETMF_QUALITY')} titleActionVariant="none">
            <Card.Body className={styles.cardBodyRelative}>
              {levelFilter?.value === 'all' ? (
                <div className={styles.noDataContainer}>
                  <EmptyState
                    variant="card"
                    defaultImgVariant="chartBar"
                    title={t('ETMF_ALL_LEVELS_UNABLE_TO_DISPLAY_CHART')}
                    subtitle={t('ETMF_ALL_LEVELS_SELECT_OTHER_LEVEL')}
                  />
                </div>
              ) : (
                <>
                  {isLoadingQuality && (
                    <div className={styles.loadingOverlay}>
                      <LoadingSpinner size={40} />
                    </div>
                  )}
                  {qualityData?.length > 0 && USER_ROLE_CURRENT !== USER_ROLE.ETMF_SPONSOR ? (
                    <BarChart
                      data={qualityData}
                      colors={[CHART_COLORS.coral, CHART_COLORS.maize]}
                      isStackingChart
                      showLegend
                      height={255}
                      yAxisMax={qualityYAxisMax}
                      yAxisMin={0}
                      tooltipOptions={{
                        outside: true,
                        hideDelay: 0,
                        padding: 8
                      }}
                      callback={(chart) => {
                        qualityChartRef.current = chart
                        // Attach legend handlers after chart is fully rendered, then reflow
                        setTimeout(() => {
                          attachQualityLegendHandlers(chart)
                          chart?.reflow()
                        }, 100)
                      }}
                    />
                  ) : (
                    <div className={styles.noDataContainer}>
                      <EmptyState
                        variant="card"
                        defaultImgVariant="chartBar"
                        title={t(isLevelSelected ? 'NO_DATA_TO_SHOW' : 'EMPTY_CHART_DATA')}
                        subtitle={
                          isLevelSelected
                            ? t(USER_ROLE_CURRENT !== USER_ROLE.ETMF_SPONSOR ? 'PLEASE_SELECT_CONTEXT' : '')
                            : undefined
                        }
                      />
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* DataGrid - Server-side mode */}
      <div className="mt-3">
        <DataGrid
          className="summary-onex-data-grid"
          isLoading={isLoadDataGridRows}
          dataTestId="etmf-summary-datagrid"
          columns={gridColumns}
          data={gridData?.elements || []}
          onChangeControl={handleDataGridControlChange}
          onColumnFiltersChange={handleColumnFiltersChange}
          filterData={filterData}
          filterDrawerOpen={true}
          manualDataLength={gridData?.totalElements || 0}
          pageCount={Math.ceil((gridData?.totalElements || 0) / pagination.pageSize)}
          title={t('DOCUMENT(S)')}
          enableGlobalFilter={true}
          onGlobalSearchChange={handleGlobalSearch}
          onClearSearch={handleClearSearch}
          showClearBtnInSearch={!!globalSearchValue}
          enablePagination={true}
          onPaginationChange={setPagination}
          enableColumnsConfiguration={true}
          rowsPerPageOptions={PAGE_SIZES_ARRAY}
          dataGridControlProps={{
            primaryActionText: t('ETMF_UPLOAD_DOCUMENTS'),
            primaryAction: () => navigateToUploadNFilePath(),
            hasActionBtn: !!(USER_ROLE_CURRENT !== USER_ROLE.ETMF_SPONSOR),
            hasViewOptions: false
          }}
          pagination={pagination}
          lockedColumnsIds={['name', 'actions']}
          onColumnOrderChange={handleColumnOrderChange}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          state={{
            pagination,
            columnOrder,
            columnPinning,
            columnVisibility
          }}
        />
      </div>
    </div>
  )
}

export default Summary
